import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../styles/Homepage.css';
import { Checkbox, formatValue, MiniNav, Toggle, AddActivity, SearchSelect, DateInput, formatDate } from './FieldComponents';
import { ArrowUpRight, Fullscreen, Maximize2, Triangle, ChevronLeft, ChevronRight, ChevronDown, PartyPopper, Calendar, CalendarClock, Clock, Clock11, CircleFadingArrowUp, EyeOff, Eye, ChevronUp, MoveRight, Languages, Luggage, SquareCheck, SquareCheckBig, BriefcaseBusiness, X, ArrowUp, Download, Database, Text, Plus } from 'lucide-react';
import { UpTrend } from './UpTrend';
import { DownTrend } from './DownTrend';
import { NoTrend } from './NoTrend';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { useNavigate, useLocation } from 'react-router';
import Modal from './Modal';
import BDDashboard from './BDDashboard';
import ModularDashboard from './ModularDashboard';
import { getCookie, setCookie } from '../utils/cookies';
import { useNavVariant } from '../App';
import { fetchNavSearchResults, toCaseLeadSelectionOptions, parseCaseLeadSelection } from '../utils/navSearch';
import { getDashboardIcon } from '../utils/dashboardIcons';
import { canNavigateTimeBilling, TIME_BILLING_PATH } from '../utils/timeBillingNavigation';
import { buildHomepageCasesCsv } from '../utils/homepageCsvExport';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.casedb.co';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getDashboardDateParams = (search = window.location.search) => {
    const params = new URLSearchParams(search);
    const dateFrom = params.get('date_from') || '';
    const dateTo = params.get('date_to') || '';

    return {
        dateFrom: ISO_DATE_PATTERN.test(dateFrom) ? dateFrom : '',
        dateTo: ISO_DATE_PATTERN.test(dateTo) ? dateTo : '',
    };
};

const formatLocalDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getYearToDateRange = (date = new Date()) => ({
    startDate: new Date(date.getFullYear(), 0, 1),
    endDate: new Date(date),
});

const getMostRecentCompletedQuarterRange = (quarter, date = new Date()) => {
    const currentQuarter = Math.floor(date.getMonth() / 3) + 1;
    const year = quarter >= currentQuarter ? date.getFullYear() - 1 : date.getFullYear();
    const startMonth = (quarter - 1) * 3;

    return {
        startDate: new Date(year, startMonth, 1),
        endDate: new Date(year, startMonth + 3, 0),
    };
};

const openCaseInNewTab = (caseId) => {
    if (!caseId) return;
    window.open(`/case/${caseId}`, '_blank', 'noopener,noreferrer');
};

export const HomePage = ({ user }) => {
    const [userId, setUserId] = useState(user.id);
    const [attorneys, setAttorneys] = useState([]);
    const [selectedAttorney, setSelectedAttorney] = useState(null);
    const [attorneyData, setAttorneyData] = useState(null);
    const navVariant = useNavVariant();
    const [myCases, setMyCases] = useState([]);
    const [recentSettlements, setRecentSettlements] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [sessionRoleId, setSessionRoleId] = useState(null);
    const [accessLevel, setAccessLevel] = useState(null);
    const [hasDataAccess, setHasDataAccess] = useState(false);
    const [loadingCases, setLoadingCases] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [viewType, setViewType] = useState(user?.access_level === 'global admin' ? 'attorney' : 'tasks'); // Admin sees data first, others see tasks
    const [dashboardNav, setDashboardNav] = useState([]);
    const [selectedDashboardId, setSelectedDashboardId] = useState(null);
    const [tasksData, setTasksData] = useState(null);
    const [showCompleted, setShowCompleted] = useState(false);
    const [assignedByMe, setAssignedByMe] = useState(false);
    const [paginatedTasks, setPaginatedTasks] = useState({ tasks: 0, assignedByMe: 0 });
    const [taskPage, setTaskPage] = useState(1);
    const [assignedTaskPage, setAssignedTaskPage] = useState(1);
    const tasksPerPage = 50;
    const [hiddenValues, setHiddenValues] = useState(true);
    const [loadingEmployeeData, setLoadingEmployeeData] = useState(false); // Separate loading for employee data
    const [initialEmployeeFetchComplete, setInitialEmployeeFetchComplete] = useState(false);
    const [practiceTypes, setPracticeTypes] = useState([]);
    const [selectedPractice, setSelectedPractice] = useState(0);
    const [canSelectAttorney, setCanSelectAttorney] = useState(false); // Only true for global admins
    const [loadingDots, setLoadingDots] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [dashboardCollapsed, setDashboardCollapsed] = useState(false);
    const [listView, setListView] = useState(() => {
        const saved = getCookie('homepage_listView');
        return saved === 'true';
    });
    const [taskView, setTaskView] = useState(() => {
        const saved = getCookie('homepage_taskView');
        return saved !== null ? saved === 'true' : true;
    });
    const [settlementListView, setSettlementListView] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [bdUsers, setBdUsers] = useState([]);
    const [selectedBdUser, setSelectedBdUser] = useState('');
    const [bdAccess, setBdAccess] = useState(false);
    // Filter states
    const [nameFilter, setNameFilter] = useState('');
    const [settlementFilter, setSettlementFilter] = useState('all'); // 'all', 'settled', 'unsettled'
    const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'Primary', 'Attorney'
    const [casePhaseFilter, setCasePhaseFilter] = useState('all');
    const [firstPrimaryFilter, setFirstPrimaryFilter] = useState('all');
    const [caseTypeFilter, setCaseTypeFilter] = useState('all');
    const [showArchivedCases, setShowArchivedCases] = useState(false);
    // New task modal states
    const [showNewTaskModal, setShowNewTaskModal] = useState(false);
    const [showNewNoteModal, setShowNewNoteModal] = useState(false);
    const [selectedCaseOrLead, setSelectedCaseOrLead] = useState(null); // stores selected key like "case_123" or "lead_456"
    const [users, setUsers] = useState([]);
    // Task detail modal states
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [hoveringTaskStatus, setHoveringTaskStatus] = useState(false);
    const [clickTimer, setClickTimer] = useState(null);
    const [editingTask, setEditingTask] = useState(false);
    const [editedTaskData, setEditedTaskData] = useState({});
    const [changingAssignee, setChangingAssignee] = useState(false);
    const [newAssignee, setNewAssignee] = useState(null);
    const [taskSortColumn, setTaskSortColumn] = useState('due'); // 'task', 'status', 'due', 'case', 'assigned'
    const [taskSortDirection, setTaskSortDirection] = useState('asc'); // 'asc' or 'desc' - default oldest first for due dates
    const [hoveredTaskRow, setHoveredTaskRow] = useState(null); // Track which task row is hovered
    const [fadingOutTaskRow, setFadingOutTaskRow] = useState(null); // Track which row is fading out
    const hoverTimeoutRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const loadCaseLeadOptions = useCallback(async (query) => {
        const results = await fetchNavSearchResults(query, { type: 'both', limit: 10 });
        return toCaseLeadSelectionOptions(results);
    }, []);

    const selectedCaseLeadTarget = useMemo(() => parseCaseLeadSelection(selectedCaseOrLead), [selectedCaseOrLead]);
    // Determine if current user should see the BD Dashboard (role 17)
    const isBD = (() => {
        if (!user) return false;
        const roleFromApi = sessionRoleId ?? user.role ?? null;
        // common shapes: user.role, user.roles (array or comma string)
        if (roleFromApi === 17 || roleFromApi === '17') return true;
        if (Array.isArray(user.roles)) {
            return user.roles.includes(17) || user.roles.includes('17');
        }
        if (typeof user.roles === 'string') {
            // comma separated or JSON
            try {
                const parsed = JSON.parse(user.roles);
                if (Array.isArray(parsed)) return parsed.includes(17) || parsed.includes('17');
            } catch (e) {
                // fallback to comma-separated
                return user.roles.split(',').map(s => s.trim()).includes('17');
            }
        }
        return false;
    })();
    const initialLoadComplete = useRef(false);
    const employeeFetchInitialized = useRef(false);
    const isGlobalAdmin = (accessLevel || user?.access_level || '').toString().toLowerCase() === 'global admin';
    const canViewAttorneyData = isGlobalAdmin || hasDataAccess;

    // Save list view preferences to cookies
    useEffect(() => {
        setCookie('homepage_listView', listView.toString(), 365);
    }, [listView]);

    useEffect(() => {
        setCookie('homepage_taskView', taskView.toString(), 365);
    }, [taskView]);

    useEffect(() => {
        if (isBD && !isGlobalAdmin) {
            document.title = "Business Development | CaseDB";
        } else if (viewType === 'bd' || viewType === 'bds') {
            document.title = "Business Development | CaseDB";
        } else if (viewType === 'tasks') {
            document.title = "Tasks | CaseDB";
        } else if (viewType === 'attorney') {
            document.title = "Dashboard | CaseDB";
        } else {
            document.title = "Dashboard | CaseDB";
        }

        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        if(screenWidth < 1200) {
            setFiltersCollapsed(true);
        } else {
            setFiltersCollapsed(false);
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [viewType, isGlobalAdmin, isBD]);

    // Fetch users for task assignment
    const fetchUsers = async () => {
        try {
            const response = await fetch('https://api.casedb.co/user.php', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success && data.users) {
                setUsers(data.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Fetch users on component mount for task assignments
    useEffect(() => {
        fetchUsers();
    }, []);

    const loadDashboardNav = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/modular_dashboard.php?action=nav`, {
                credentials: 'include',
            });
            if (!response.ok) {
                return;
            }

            const data = await response.json();
            if (!data.success) {
                return;
            }

            const dashboards = Array.isArray(data.dashboards) ? data.dashboards : [];
            setDashboardNav(dashboards);
            setSelectedDashboardId((currentId) => currentId || dashboards.find((item) => item.is_default)?.id || dashboards[0]?.id || null);
        } catch (error) {
            console.warn('Unable to load dashboard nav', error);
        }
    }, []);

    useEffect(() => {
        loadDashboardNav();
    }, [loadDashboardNav]);

    // Set default date range to year-to-date (calculate once)
    const [dateFromFilter, setDateFromFilter] = useState(() => {
        const { dateFrom } = getDashboardDateParams();
        if (dateFrom) {
            return dateFrom;
        }

        return formatLocalDateInputValue(getYearToDateRange().startDate);
    });
    const [dateToFilter, setDateToFilter] = useState(() => {
        const { dateTo } = getDashboardDateParams();
        if (dateTo) {
            return dateTo;
        }

        return formatLocalDateInputValue(getYearToDateRange().endDate);
    });
    const [selectedDateRange, setSelectedDateRange] = useState(() => {
        const { dateFrom, dateTo } = getDashboardDateParams();
        return dateFrom || dateTo ? 'custom' : 'full_year';
    }); // 'full_year', 'q1', 'q2', 'q3', 'q4', 'ytd', '30d', '90d', '6m', '1y', 'custom'
    const [taskOption, setSelectedTaskOption] = useState('All Due Dates'); // 'On or Before Today', 'All Due Dates', 'Due Today', 'Next 7 Days', 'Next 30 Days'
    const casesPerPage = 5;
    const hasValidCustomDateRange = Boolean(
        dateFromFilter &&
        dateToFilter &&
        dateFromFilter <= dateToFilter
    );

    // Helper function to mask currency values when hidden
    const maskCurrency = (value) => {
        if (hiddenValues) {
            return '$#,###.##';
        }
        return formatValue(value, 'currency');
    };

    // Helper function to get today's date at midnight
    const getTodayMidnight = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    // Helper function to filter tasks by date range
    const filterTasksByDate = (task) => {
        // "All Due Dates" shows everything
        if (taskOption === 'All Due Dates') {
            return true;
        }
        
        // For other filters, require a due date
        if (!task.deadline_due) return false;
        
        const dueDate = new Date(task.deadline_due);
        dueDate.setHours(0, 0, 0, 0);
        const today = getTodayMidnight();
        
        switch (taskOption) {
            case 'On or Before Today':
                // Show incomplete tasks that are due today or earlier
                return dueDate <= today && !task.is_completed;
            case 'Due Today':
                // Show tasks due today (completed or not)
                return dueDate.getTime() === today.getTime();
            case 'Next 7 Days': {
                const next7Days = new Date(today);
                next7Days.setDate(next7Days.getDate() + 7);
                // Show incomplete tasks due from today through next 7 days
                return dueDate >= today && dueDate <= next7Days && !task.is_completed;
            }
            case 'Next 30 Days': {
                const next30Days = new Date(today);
                next30Days.setDate(next30Days.getDate() + 30);
                // Show incomplete tasks due from today through next 30 days
                return dueDate >= today && dueDate <= next30Days && !task.is_completed;
            }
            default:
                return true;
        }
    };

    // Calculate task counts for each filter option
    const getTaskNotifications = () => {
        if (!tasksData || !tasksData.tasks) return [0, 0, 0, 0, 0];
        
        const today = getTodayMidnight();
        const allTasks = tasksData.tasks;
        
        // All Due Dates - all tasks
        const allCount = allTasks.length;
        
        // On or Before Today - incomplete tasks due today or earlier
        const onOrBeforeCount = allTasks.filter(task => {
            if (!task.deadline_due || task.is_completed) return false;
            const dueDate = new Date(task.deadline_due);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate <= today;
        }).length;
        
        // Due Today - tasks due today
        const dueTodayCount = allTasks.filter(task => {
            if (!task.deadline_due) return false;
            const dueDate = new Date(task.deadline_due);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate.getTime() === today.getTime();
        }).length;
        
        // Next 7 Days - incomplete tasks due from today through next 7 days
        const next7DaysCount = allTasks.filter(task => {
            if (!task.deadline_due || task.is_completed) return false;
            const dueDate = new Date(task.deadline_due);
            dueDate.setHours(0, 0, 0, 0);
            const next7Days = new Date(today);
            next7Days.setDate(next7Days.getDate() + 7);
            return dueDate >= today && dueDate <= next7Days;
        }).length;
        
        // Next 30 Days - incomplete tasks due from today through next 30 days
        const next30DaysCount = allTasks.filter(task => {
            if (!task.deadline_due || task.is_completed) return false;
            const dueDate = new Date(task.deadline_due);
            dueDate.setHours(0, 0, 0, 0);
            const next30Days = new Date(today);
            next30Days.setDate(next30Days.getDate() + 30);
            return dueDate >= today && dueDate <= next30Days;
        }).length;
        
        return [allCount, onOrBeforeCount, dueTodayCount, next7DaysCount, next30DaysCount];
    };

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            if (dateFromFilter) {
                params.append('date_from', dateFromFilter);
            }

            const response = await fetch(`https://api.casedb.co/dashboard.php?${params.toString()}`);
            const data = await response.json();
            if (data.success) {
                setAttorneys(data.attorneys);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const fetchCaseTypes = async () => {
        const response = await fetch('https://api.casedb.co/case_types.php');
        const data = await response.json();
        setPracticeTypes(data.case_types);
    }

    useEffect(() => {
        fetchData();
    }, [dateFromFilter]);

    useEffect(() => {
        fetchCaseTypes();
        // Don't call fetchAttorneyData here - let the debounced effect handle it
    }, []);

    // Animated loading dots effect
    useEffect(() => {
        if (loadingCases) {
            const interval = setInterval(() => {
                setLoadingDots(prev => {
                    if (prev === '...') return '.';
                    return prev + '.';
                });
            }, 500);
            return () => clearInterval(interval);
        } else {
            setLoadingDots('');
        }
    }, [loadingCases]);

    // Request deduplication to prevent race conditions
    const [currentRequest, setCurrentRequest] = useState(null);
    const [employeeDataRequest, setEmployeeDataRequest] = useState(null);
    
    // Sync attorney-selection capability to access level (only global admins)
    useEffect(() => {
        setCanSelectAttorney(accessLevel === 'global admin');
    }, [accessLevel]);
    
    // Reset task pagination and refetch when switching between views or toggling completed tasks
    useEffect(() => {
        // Skip on initial mount - let the initialization effect handle it
        if (!initialLoadComplete.current) return;
        
        setTaskPage(1);
        setAssignedTaskPage(1);
        if (viewType === 'tasks') {
            fetchTasksData('tasks', false); // Don't reload myCases when switching task views
        }
    }, [assignedByMe, showCompleted]);

    // Fetch tasks data quickly on load
    const fetchTasksData = async (mode = 'tasks', updateMyCases = true) => {
        setLoadingTasks(true);
        setLoadingCases(true);
        
        try {
            const params = new URLSearchParams();
            params.append('mode', mode); // 'tasks', 'data', or 'both'
            params.append('task_page', taskPage);
            params.append('assigned_page', assignedTaskPage);
            params.append('show_assigned', assignedByMe);
            params.append('show_completed', showCompleted);
            
            const response = await fetch(`https://api.casedb.co/employee.php?${params.toString()}`, { 
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success && data.tasks_data) {
                // Always update tasks data
                setTasksData(prevData => ({
                    ...prevData,
                    tasks: data.tasks_data.tasks || [],
                    task_count: data.tasks_data.task_count || 0,
                    tasks_assigned_count: data.tasks_data.tasks_assigned_count || 0,
                    pending_tasks: data.tasks_data.pending_tasks || 0,
                    completed_tasks: data.tasks_data.completed_tasks || 0,
                    overdue_tasks: data.tasks_data.overdue_tasks || 0
                }));
                
                // Only update myCases on initial load
                if (updateMyCases) {
                    setMyCases(data.tasks_data.my_cases || []);
                }
                
                setPaginatedTasks({
                    tasks: data.tasks_data.task_count || 0,
                    assignedByMe: data.tasks_data.tasks_assigned_count || 0
                });
                if (updateMyCases) {
                    console.log('My Cases loaded:', data.tasks_data.my_cases?.length || 0, 'cases');
                }
            }

            if (data.role !== undefined && data.role !== null) {
                setSessionRoleId(data.role);
            }
            
            // Store access level
            if (data.access_level) {
                setAccessLevel(data.access_level);
                setCanSelectAttorney(data.access_level === 'global admin');
            }
            
            // Return both data_access and access_level
            return {
                hasDataAccess: data.data_access || false,
                accessLevel: data.access_level || null
            };
        } catch (error) {
            console.error('Error fetching tasks data:', error);
            return { hasDataAccess: false, accessLevel: null };
        } finally {
            setLoadingCases(false);
            setLoadingTasks(false);
        }
    };
    
    // Fetch employee/attorney data separately (slower)
    const fetchEmployeeData = async () => {
        // Abort any existing request
        if (employeeDataRequest) {
            employeeDataRequest.abort();
        }
        
        const controller = new AbortController();
        setEmployeeDataRequest(controller);
        setLoadingEmployeeData(true);
        
        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('mode', 'data'); // Request only employee data
            
            if (selectedAttorney) {
                params.append('user_id', selectedAttorney);
            }
            if (Number(selectedPractice) > 0) {
                params.append('case_type_id', String(Number(selectedPractice)));
            }
            if (dateFromFilter) {
                params.append('date_from', dateFromFilter);
            }
            if (dateToFilter) {
                params.append('date_to', dateToFilter);
            }
            
            const queryString = params.toString();
            
            const response = await fetch(`https://api.casedb.co/employee.php?${queryString}`, { 
                credentials: 'include',
                signal: controller.signal
            });
            
            const data = await response.json();
            
            if (data.success) {
                setAttorneyData({
                    filters: data.filters || {},
                    employees: data.employees || [],
                    totals: data.totals || {},
                    employee: data.employee || null
                });
                // Only global admins can select attorneys
                setCanSelectAttorney(accessLevel === 'global admin');
                
                // Process settlements - combine all from all employees
                let allSettlements = [];
                if (data.employees && Array.isArray(data.employees)) {
                    data.employees.forEach(emp => {
                        if (emp.settled_cases && Array.isArray(emp.settled_cases)) {
                            allSettlements = allSettlements.concat(emp.settled_cases);
                        }
                    });
                    // Sort by date descending
                    allSettlements.sort((a, b) => new Date(b.settlement_date) - new Date(a.settlement_date));
                }
                setRecentSettlements(allSettlements);
            }

            if (data.role !== undefined && data.role !== null) {
                setSessionRoleId(data.role);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Employee data fetch was cancelled');
            } else {
                console.error('Error fetching employee data:', error);
            }
        } finally {
            setLoadingEmployeeData(false);
            setEmployeeDataRequest(null);
        }
    };
    
    // Legacy function for backwards compatibility - now just calls the two separate fetches
    const fetchAttorneyData = async () => {
        await fetchEmployeeData();
        // Don't fetch employee data on initial load - only when user switches to that view
    };

    const nextCases = () => {
        const totalPages = Math.ceil(filteredCasesForDisplay.length / casesPerPage);
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevCases = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Helper function to get status order for sorting
    const getStatusOrder = (task) => {
        const completionInfo = getTaskCompletionStatus(task, parseInt(userId));
        if (completionInfo.status === 'complete') return 3; // Completed last
        if (task.is_overdue && completionInfo.status !== 'complete') return 0; // Overdue first
        if (completionInfo.status === 'partial') return 1; // Partial second
        return 2; // Incomplete third
    };

    // Helper function to sort tasks
    const sortTasks = (tasks, column, direction) => {
        const sorted = [...tasks].sort((a, b) => {
            let aVal, bVal;
            
            switch (column) {
                case 'task':
                    // Sort by subject alphabetically
                    aVal = (a.subject || '').toLowerCase();
                    bVal = (b.subject || '').toLowerCase();
                    break;
                case 'status':
                    // Sort by status order (overdue, partial, incomplete, completed)
                    aVal = getStatusOrder(a);
                    bVal = getStatusOrder(b);
                    break;
                case 'due':
                    // Sort by due date chronologically
                    aVal = a.deadline_due ? new Date(a.deadline_due).getTime() : Infinity;
                    bVal = b.deadline_due ? new Date(b.deadline_due).getTime() : Infinity;
                    break;
                case 'case':
                    // Sort by case or lead name alphabetically, with empty values last
                    aVal = (a.case_name || a.lead_name || '').toLowerCase();
                    bVal = (b.case_name || b.lead_name || '').toLowerCase();
                    // If one is empty and the other isn't, empty goes last (or first if desc)
                    if (aVal === '' && bVal !== '') return direction === 'asc' ? 1 : -1;
                    if (aVal !== '' && bVal === '') return direction === 'asc' ? -1 : 1;
                    break;
                case 'assigned':
                    // Sort by assignee or assigned by name alphabetically
                    if (assignedByMe) {
                        aVal = (a.assigned_to_name?.[0] || '').toLowerCase();
                        bVal = (b.assigned_to_name?.[0] || '').toLowerCase();
                    } else {
                        aVal = (a.author_name || '').toLowerCase();
                        bVal = (b.author_name || '').toLowerCase();
                    }
                    break;
                default:
                    return 0;
            }
            
            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    };

    // Determine which data to display: Tasks or Settlements
    let displayData = [];
    let isSettlementsView = false;
    let isTasksView = false;
    
    if (viewType === 'tasks' && tasksData) {
        // Filter tasks based on taskOption
        isTasksView = true;
        displayData = tasksData.tasks.filter(filterTasksByDate);
    } else if (viewType === 'attorney') {
        // Attorney view shows settlements
        displayData = recentSettlements;
        isSettlementsView = true;
    }

    // Filter tasks separately from cases
    const filteredTasks = isTasksView ? sortTasks(displayData, taskSortColumn, taskSortDirection) : [];
    
    // Filter cases/settlements based on current filter settings (independent of task filters)
    const filteredCases = !isTasksView ? displayData.filter(item => {
        // Name filter - works for both cases and settlements
        const searchName = isSettlementsView ? 
            (item.case_name?.toLowerCase() || '') + ' ' + (item.client_name?.toLowerCase() || '') + ' ' + (item.attorney_name?.toLowerCase() || '') :
            (item.case_name?.toLowerCase() || '') + ' ' + ((item.first_name && item.last_name) ? `${item.first_name} ${item.last_name}`.toLowerCase() : '');
        
        const matchesName = !nameFilter || searchName.includes(nameFilter.toLowerCase());
        
        // Settlement filter - works for both cases and settlements
        const matchesSettlement = settlementFilter === 'all' ||
            (settlementFilter === 'settled' && item.settlement_amount > 0) ||
            (settlementFilter === 'unsettled' && (item.settlement_amount === "0.00" || item.settlement_amount === 0));
        
        // Role filter - only applies to My Cases view
        const matchesRole = isSettlementsView || roleFilter === 'all' ||
            (item.role_names && item.role_names.includes(roleFilter));
        
        return matchesName && matchesSettlement && matchesRole;
    }) : [];

    // Use appropriate filtered list based on view type
    const filteredItems = isTasksView ? filteredTasks : filteredCases;

    // Calculate pagination based on view type
    let casesForPagination = [];
    if (isTasksView) {
        // In tasks view, show myCases
        casesForPagination = myCases;
    } else if (isSettlementsView) {
        // In attorney view, show filtered settlements
        casesForPagination = filteredCases;
    }
    
    const availableTaskCasePhaseOptions = myCases.reduce((acc, item) => {
        if (!item?.phase_id || !item?.phase_name) {
            return acc;
        }
        if (!acc.some((phase) => String(phase.id) === String(item.phase_id))) {
            acc.push({ id: item.phase_id, phase: item.phase_name });
        }
        return acc;
    }, []).sort((a, b) => String(a.phase).localeCompare(String(b.phase)));

    const availableTaskCasePrimaryOptions = myCases.reduce((acc, item) => {
        if (!item?.first_primary_user_id || !item?.first_primary_name) {
            return acc;
        }
        if (!acc.some((primaryUser) => String(primaryUser.id) === String(item.first_primary_user_id))) {
            acc.push({ id: item.first_primary_user_id, name: item.first_primary_name });
        }
        return acc;
    }, []).sort((a, b) => String(a.name).localeCompare(String(b.name)));

    const availableTaskCaseTypeOptions = myCases.reduce((acc, item) => {
        if (!item?.case_type_id || !item?.type_name) {
            return acc;
        }
        if (!acc.some((caseType) => String(caseType.id) === String(item.case_type_id))) {
            acc.push({ id: item.case_type_id, name: item.type_name });
        }
        return acc;
    }, []).sort((a, b) => String(a.name).localeCompare(String(b.name)));

    // Filter cases for "My Cases" or "Settlements" section
    const filteredCasesForDisplay = casesForPagination.filter(item => {
        const searchName = isSettlementsView ? 
            (item.case_name?.toLowerCase() || '') + ' ' + (item.client_name?.toLowerCase() || '') + ' ' + (item.attorney_name?.toLowerCase() || '') :
            (item.case_name?.toLowerCase() || '') + ' ' + ((item.first_name && item.last_name) ? `${item.first_name} ${item.last_name}`.toLowerCase() : '');
        
        const matchesName = !nameFilter || searchName.includes(nameFilter.toLowerCase());
        
        const matchesSettlement = settlementFilter === 'all' ||
            (settlementFilter === 'settled' && item.settlement_amount > 0) ||
            (settlementFilter === 'unsettled' && (item.settlement_amount === "0.00" || item.settlement_amount === 0));
        
        const matchesRole = isSettlementsView || roleFilter === 'all' ||
            (item.role_names && item.role_names.includes(roleFilter));

        const matchesPhase = isSettlementsView || casePhaseFilter === 'all' ||
            String(item.phase_id) === String(casePhaseFilter);

        const matchesFirstPrimary = isSettlementsView || firstPrimaryFilter === 'all' ||
            String(item.first_primary_user_id) === String(firstPrimaryFilter);

        const matchesCaseType = isSettlementsView || caseTypeFilter === 'all' ||
            String(item.case_type_id) === String(caseTypeFilter);

        const matchesArchived = isSettlementsView || showArchivedCases || String(item.phase_id) !== '26';
        
        return matchesName && matchesSettlement && matchesRole && matchesPhase && matchesFirstPrimary && matchesCaseType && matchesArchived;
    });

    // Calculate pagination
    const totalPages = Math.ceil(filteredCasesForDisplay.length / casesPerPage);
    const startIndex = (currentPage - 1) * casesPerPage;
    const endIndex = Math.min(startIndex + casesPerPage, filteredCasesForDisplay.length);
    const visibleItems = filteredCasesForDisplay.slice(startIndex, endIndex);

    // Reset pagination when filters change (but NOT when taskOption changes)
    useEffect(() => {
        setCurrentPage(1);
    }, [nameFilter, settlementFilter, roleFilter, casePhaseFilter, firstPrimaryFilter, caseTypeFilter, showArchivedCases, dateFromFilter, dateToFilter]);

    // Ensure current page doesn't exceed total pages
    useEffect(() => {
        const totalPages = Math.ceil(filteredCasesForDisplay.length / casesPerPage);
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [filteredCasesForDisplay.length, currentPage, casesPerPage]);

    // Detect URL parameter for new task modal or task detail modal
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('task') === 'true') {
            setShowNewTaskModal(true);
            fetchUsers();
        } else if (params.get('note') === 'true') {
            setShowNewNoteModal(true);
            fetchUsers();
        }
        // Check for key parameter to show task detail
        const taskKey = params.get('key');
        if (taskKey) {
            fetchTaskDetail(taskKey);
        }
    }, [location]);

    // Fetch task detail by ID
    const fetchTaskDetail = async (taskId) => {
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php?id=${taskId}`, {
                credentials: 'include'
            });
            const result = await response.json();
            if (result.success && result.feed && result.feed.length > 0) {
                setSelectedTask(result.feed[0]);
                setShowTaskModal(true);
                // Fetch users and cases/leads for the edit form
                fetchUsers();
            } else {
                console.error('Task not found');
            }
        } catch (error) {
            console.error('Error fetching task detail:', error);
        }
    };

    useEffect(() => {
        console.log(selectedTask);
    }, [selectedTask]);

    // Handle task click - opens modal after delay (to detect double click)
    const handleTaskClick = (task, event) => {
        // Prevent text selection on double click
        if (event.detail === 2) {
            event.preventDefault();
            return;
        }

        // Clear any existing timer
        if (clickTimer) {
            clearTimeout(clickTimer);
            setClickTimer(null);
        }

        // Set a new timer to open modal after delay
        const timer = setTimeout(() => {
            setSelectedTask(task);
            setShowTaskModal(true);
            setClickTimer(null);
        }, 250); // 250ms delay to detect double click

        setClickTimer(timer);
    };

    // Handle task double click - navigates to case/lead if available
    const handleTaskDoubleClick = (task, event) => {
        event.preventDefault();
        
        // Clear the single click timer
        if (clickTimer) {
            clearTimeout(clickTimer);
            setClickTimer(null);
        }

        // Navigate to case or lead
        if (task.case_id > 0) {
            window.open(`/case/${task.case_id}?key=${task.task_id}`, '_blank', 'noopener,noreferrer');
        } else if (task.lead_id > 0) {
            window.open(`/intake/${task.lead_id}?key=${task.task_id}`, '_blank', 'noopener,noreferrer');
        }
        // If no case or lead, do nothing on double click
    };

    // Close task modal
    const closeTaskModal = () => {
        setShowTaskModal(false);
        setSelectedTask(null);
        setHoveringTaskStatus(false);
        setEditingTask(false);
        setEditedTaskData({});
        setChangingAssignee(false);
        setNewAssignee(null);
        // Remove key parameter from URL if present
        const params = new URLSearchParams(location.search);
        if (params.has('key')) {
            params.delete('key');
            navigate({ search: params.toString() }, { replace: true });
        }
    };

    // Save assignee change (for non-authors)
    const saveAssigneeChange = async (assigneeIdToAdd = null, assigneeIdToRemove = null) => {
        try {
            const formData = new FormData();
            formData.append('edit', 'true');
            formData.append('id', selectedTask.id || selectedTask.task_id);
            
            if (assigneeIdToRemove) {
                // Remove a specific assignee
                formData.append('action', 'remove_assignee');
                formData.append('assignee_id', assigneeIdToRemove);
            } else {
                // Add a new assignee
                const assigneeToAdd = assigneeIdToAdd || newAssignee;
                
                if (!assigneeToAdd) {
                    return;
                }
                
                // Check if already assigned
                if (selectedTask.assigned_to_id?.includes(Number(assigneeToAdd))) {
                    alert('This user is already assigned to this task');
                    return;
                }
                
                formData.append('action', 'add_assignee');
                formData.append('assignee_id', assigneeToAdd);
            }

            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const result = await response.json();
            if (result.success) {
                // Refresh the task list first
                await fetchTasksData();
                
                // Then refresh the specific task data for the modal
                const taskId = selectedTask.id || selectedTask.task_id;
                const taskResponse = await fetch(`https://api.casedb.co/activity_feed.php?id=${taskId}`, {
                    credentials: 'include'
                });
                const taskResult = await taskResponse.json();
                
                if (taskResult.success && taskResult.feed?.[0]) {
                    setSelectedTask(taskResult.feed[0]);
                }
                
                setChangingAssignee(false);
                setNewAssignee(null);
            } else {
                alert(`Failed to update assignee: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error updating assignee:', error);
            alert('Failed to update assignee. Please try again.');
        }
    };

    // Save task edit
    const saveTaskEdit = async () => {
        try {
            const formData = new FormData();
            formData.append('edit', 'true');
            formData.append('id', selectedTask.id || selectedTask.task_id);
            formData.append('subject', editedTaskData.subject || selectedTask.subject);
            formData.append('content', editedTaskData.content || selectedTask.content);
            formData.append('type', 'tasks');
            
            // Add task assignment and due date
            if (editedTaskData.task !== undefined) {
                formData.append('task', editedTaskData.task);
            } else if (selectedTask.assigned_to_id?.[0]) {
                formData.append('task', selectedTask.assigned_to_id[0]);
            }
            
            if (editedTaskData.due_date !== undefined) {
                formData.append('due_date', editedTaskData.due_date);
            } else if (selectedTask.deadline_due) {
                formData.append('due_date', selectedTask.deadline_due.split(' ')[0]);
            }
            
            if (editedTaskData.post_date !== undefined) {
                formData.append('post_date', editedTaskData.post_date);
            } else if (selectedTask.deadline_post) {
                formData.append('post_date', selectedTask.deadline_post.split(' ')[0]);
            }
            
            // Handle case/lead update
            if (editedTaskData.case_lead_id !== undefined) {
                if (editedTaskData.case_lead_id) {
                    const parsedSelection = parseCaseLeadSelection(editedTaskData.case_lead_id);
                    if (parsedSelection) {
                        if (parsedSelection.type === 'case') {
                            formData.append('case_id', parsedSelection.id);
                            formData.append('lead_id', '');
                        } else if (parsedSelection.type === 'lead') {
                            formData.append('lead_id', parsedSelection.id);
                            formData.append('case_id', '');
                        }
                    }
                } else {
                    // Clear both if empty selection
                    formData.append('case_id', '');
                    formData.append('lead_id', '');
                }
            } else {
                // Keep existing values
                if (selectedTask.case_id) formData.append('case_id', selectedTask.case_id);
                if (selectedTask.lead_id) formData.append('lead_id', selectedTask.lead_id);
            }

            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const result = await response.json();
            if (result.success) {
                // Refresh the task list first
                await fetchTasksData();
                
                // Then refresh the specific task data for the modal
                const taskId = selectedTask.id || selectedTask.task_id;
                const taskResponse = await fetch(`https://api.casedb.co/activity_feed.php?id=${taskId}`, {
                    credentials: 'include'
                });
                const taskResult = await taskResponse.json();
                
                if (taskResult.success && taskResult.feed?.[0]) {
                    setSelectedTask(taskResult.feed[0]);
                }
                
                setEditingTask(false);
                setEditedTaskData({});
            } else {
                alert(`Failed to save task: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error saving task:', error);
            alert('Failed to save task. Please try again.');
        }
    };

    // Toggle task completion status
    // Helper to determine task completion status
    const getTaskCompletionStatus = (task, currentUserId) => {
        if (!task.deadline_assignees) return { status: 'incomplete', userCompleted: false, isAssignee: false };
        
        const assignees = JSON.parse(task.deadline_assignees || '[]');
        const completionStatus = task.deadline_completion_status ? JSON.parse(task.deadline_completion_status) : {};
        
        if (assignees.length === 0) {
            // Legacy single-assignee task
            return {
                status: task.deadline_done ? 'complete' : 'incomplete',
                userCompleted: !!task.deadline_done,
                isAssignee: false
            };
        }
        
        // Check if current user is an assignee
        const isAssignee = assignees.includes(currentUserId) || assignees.includes(String(currentUserId));
        
        // Check how many assignees have completed
        let completedCount = 0;
        let userCompleted = false;
        
        for (const assigneeId of assignees) {
            if (completionStatus[assigneeId]) {
                completedCount++;
            }
            if ((assigneeId === currentUserId || assigneeId === String(currentUserId)) && completionStatus[assigneeId]) {
                userCompleted = true;
            }
        }
        
        let status = 'incomplete';
        if (completedCount === assignees.length) {
            status = 'complete';
        } else if (completedCount > 0) {
            status = 'partial';
        }
        
        return { status, userCompleted, completedCount, totalCount: assignees.length, isAssignee };
    };

    const toggleTaskCompletion = async (taskId, currentStatus) => {
        try {
            const response = await fetch(`https://api.casedb.co/activity_feed.php`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: taskId,
                    done: !currentStatus
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update task status');
            }

            const result = await response.json();

            if (result.success) {
                // Refetch the task to get 100% accurate data from the server
                const taskResponse = await fetch(`https://api.casedb.co/activity_feed.php?id=${taskId}`, {
                    credentials: 'include'
                });
                const taskResult = await taskResponse.json();
                
                if (taskResult.success && taskResult.feed && taskResult.feed.length > 0) {
                    // Update with fresh data from server
                    setSelectedTask(taskResult.feed[0]);
                }
                
                // Refresh tasks data to update the list
                fetchTasksData(assignedByMe ? 'assignedByMe' : 'tasks', false);
            } else {
                console.error('Failed to update task status:', result.message);
            }
        } catch (err) {
            console.error('Error updating task status:', err);
        }
    };

    // Close modal and clear URL param
    const closeNewTaskModal = () => {
        setShowNewTaskModal(false);
        setSelectedCaseOrLead(null);
        navigate('/dashboard', { replace: true });
    };

    // Close note modal
    const closeNewNoteModal = () => {
        setShowNewNoteModal(false);
        setSelectedCaseOrLead(null);
    };

    // Refresh feed after task creation (placeholder - may not be needed)
    const refreshAfterTaskCreation = () => {
        closeNewTaskModal();
        // Optionally refresh tasks data
        if (viewType === 'tasks') {
            fetchTasksData('tasks');
        }
    };

    // Refresh feed after note creation
    const refreshAfterNoteCreation = () => {
        closeNewNoteModal();
        // Optionally refresh tasks data
        if (viewType === 'tasks') {
            fetchTasksData('tasks');
        }
    };

    // Format datetime to user's local timezone
    const formatLastModified = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return ["", "", "", "", "", ""];

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        try {
            const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString);
            const isDateTimeWithSpace = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
            const isIsoWithoutZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(dateString);

            let normalized = dateString;
            if (!hasTimeZone) {
                if (isDateTimeWithSpace) {
                    normalized = `${dateString.replace(' ', 'T')}Z`;
                } else if (isDateOnly) {
                    normalized = `${dateString}T00:00:00Z`;
                } else if (isIsoWithoutZone) {
                    normalized = `${dateString}Z`;
                }
            }

            let parsedDate = new Date(normalized);

            if (isNaN(parsedDate.getTime())) {
                parsedDate = new Date(`${dateString}T00:00:00Z`);
            }

            if (isNaN(parsedDate.getTime())) {
                return ["", "", "", "", "", ""];
            }

            const fallbackTimeZone = "America/Chicago";
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || fallbackTimeZone;
            let zonedTime;
            try {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: userTimeZone }));
            } catch (e) {
                zonedTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: fallbackTimeZone }));
            }

            const iso = parsedDate.toISOString();
            const longDate = `${months[zonedTime.getMonth()]} ${zonedTime.getDate()}, ${zonedTime.getFullYear()}`;
            const shortDate = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()}`;
            const shortFormatted = `${zonedTime.getDate()} ${months[zonedTime.getMonth()].slice(0, 3)} ${zonedTime.getFullYear()}`;
            const hours = zonedTime.getHours();
            const minutes = zonedTime.getMinutes();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 || 12;
            const minutePadded = String(minutes).padStart(2, '0');
            const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
            const dateTimeFormatted = `${zonedTime.getMonth() + 1}/${zonedTime.getDate()}/${zonedTime.getFullYear()} ${timeFormatted}`;

            return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
        } catch (e) {
            console.error("Invalid date:", dateString, e);
            return ["", "", "", "", "", ""];
        }
    };

    const formatDatePreservingDay = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return '';

        const monthsShort = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) {
            return dateString;
        }

        const [, year, month, day] = match;
        const monthIndex = Number(month) - 1;

        if (monthIndex < 0 || monthIndex >= monthsShort.length) {
            return dateString;
        }

        return `${monthsShort[monthIndex]} ${day}, ${year}`;
    };

    // Export filtered cases to CSV
    const exportCasesToCSV = () => {
        // Use the same filtered cases that are displayed
        const casesToExport = filteredCasesForDisplay;
        
        if (casesToExport.length === 0) {
            alert('No cases to export');
            return;
        }
        
        const csvContent = buildHomepageCasesCsv(casesToExport, {
            isSettlementsView,
            formatDatePreservingDay,
            formatLastModified,
            formatCurrency: (value) => formatValue(value, 'currency'),
        });
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `${isSettlementsView ? 'settlements' : 'my_cases'}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Fetch appropriate data on initial load based on data access
    useEffect(() => {
        const initializeData = async () => {
            // Fetch tasks first and check if user has data access
            const { hasDataAccess: dataAccess, accessLevel: userAccessLevel } = await fetchTasksData('tasks');
            
            // Store in state
            setHasDataAccess(dataAccess);
            
            if (userAccessLevel === 'global admin' || dataAccess) {
                // Users with employee data access should land in the data view
                setViewType('attorney');
                await fetchEmployeeData();
            } else {
                // Everyone else sees tasks first
                setViewType('tasks');
            }
            
            // Mark initial load as complete
            employeeFetchInitialized.current = true;
            initialLoadComplete.current = true;
            setInitialEmployeeFetchComplete(true);
        };
        
        initializeData();
    }, []);

    // Refetch tasks when pagination changes
    useEffect(() => {
        // Skip on initial mount - let the initialization effect handle it
        if (!initialLoadComplete.current) return;
        
        if (viewType === 'tasks') {
            fetchTasksData('tasks', false); // Don't reload myCases on pagination
        }
    }, [taskPage, assignedTaskPage]);
    
    // Debounced effect to prevent rapid successive API calls
    // Only fetch if user has data access OR is viewing attorney data
    useEffect(() => {
        if (!employeeFetchInitialized.current) {
            return;
        }

        // Only fetch employee data if user has access or is global admin
        const shouldFetchData = canViewAttorneyData;
        
        if (!shouldFetchData) {
            return;
        }
        
        const timeoutId = setTimeout(() => {
            fetchAttorneyData();
        }, 500); // 500ms debounce - increased to reduce API calls
        
        return () => {
            clearTimeout(timeoutId);
            // Cancel any ongoing request when dependencies change
            if (employeeDataRequest) {
                employeeDataRequest.abort();
                setEmployeeDataRequest(null);
            }
        };
    }, [selectedAttorney, selectedPractice, dateFromFilter, dateToFilter, canViewAttorneyData]);

    // Cleanup effect to cancel requests on component unmount
    useEffect(() => {
        return () => {
            // Cancel any ongoing request when component unmounts
            if (currentRequest) {
                currentRequest.abort();
                setCurrentRequest(null);
            }
            if (employeeDataRequest) {
                employeeDataRequest.abort();
                setEmployeeDataRequest(null);
            }
        };
    }, []);

    useEffect(() => {
        const fetchBdAccess = async () => {
            try {
                const resp = await fetch('https://api.casedb.co/bddash.php?access_check=1', { credentials: 'include' });
                const json = await resp.json();
                if (json?.success) {
                    setBdAccess(Boolean(json?.bd_access));
                    if (Array.isArray(json?.users)) {
                        setBdUsers(json.users);
                        if (!selectedBdUser && json.users.length > 0) {
                            setSelectedBdUser(String(json.users[0].id));
                        }
                    }
                }
            } catch (e) {
                console.error('BD access check failed', e);
            }
        };
        fetchBdAccess();
    }, []);

    return (
        <div className={`page-container homepage ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            {(loadingCases && !attorneyData) ? (
                <div
                    style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flex: '1 0 auto',
                        height: '100%',
                        fontSize: '18px'
                    }}
                >
                    Fetching data{loadingDots}
                </div>
            ) : (
                <>
                    <div id='page-header'>
                        <h2>{attorneyData?.employee ? `Welcome, ${String(attorneyData?.employee).split(" ")[0]}` : 'Tasks'}</h2>
                        <div className='this-or-that' style={{ backgroundColor: 'var(--background-color)' }}>
                                <div
                                    className={`tot-option ${ viewType === 'tasks' ? 'active' : '' }`}
                                    onClick={() => setViewType('tasks')}
                                    title='Tasks'
                                >
                                    <SquareCheckBig />
                                </div>

                                {canNavigateTimeBilling(accessLevel ?? user?.access_level) && (
                                    <button
                                        type="button"
                                        className="tot-option homepage-time-billing-option"
                                        onClick={() => navigate(TIME_BILLING_PATH)}
                                        title="Time & Billing"
                                        aria-label="Time & Billing"
                                    >
                                        <Clock />
                                    </button>
                                )}

                                {dashboardNav.map((dashboardItem) => {
                                    const DashboardIcon = getDashboardIcon(dashboardItem.icon_key);
                                    const isActive = viewType === 'dashboard' && selectedDashboardId === dashboardItem.id;

                                    return (
                                        <div
                                            key={dashboardItem.id}
                                            className={`tot-option ${isActive ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedDashboardId(dashboardItem.id);
                                                setViewType('dashboard');
                                            }}
                                            title={dashboardItem.name}
                                        >
                                            <DashboardIcon />
                                        </div>
                                    );
                                })}

                                {canViewAttorneyData && (
                                    <div
                                        className={`tot-option${ viewType === 'attorney' ? ' active' : '' }`}
                                        onClick={() => setViewType('attorney')}
                                        title='Attorney Data'
                                    >
                                        <BriefcaseBusiness />
                                    </div>
                                )}

                                {(isGlobalAdmin || bdAccess) && (
                                    <>
                                        <div
                                            className={`tot-option${ viewType === 'bd' ? ' active' : '' }`}
                                            onClick={() => setViewType('bd')}
                                            title='BD Report'
                                        >
                                            <Database />
                                        </div>

                                        <div
                                            className={`tot-option${ viewType === 'bds' ? ' active' : '' }`}
                                            onClick={() => setViewType('bds')}
                                            title='BD Summary'
                                        >
                                            <Text />
                                        </div>
                                    </>
                                )}

                                <div
                                    className={`tot-option ${ viewType === 'dashboard-create' ? 'active' : '' }`}
                                    onClick={() => setViewType('dashboard-create')}
                                    title='Create Dashboard'
                                >
                                    <Plus />
                                </div>
                            </div>
                        {(isGlobalAdmin || bdAccess) && (viewType === 'bd' || viewType === 'bds') && (
                            <div className='filter-group'>
                                <div className='select-wrapper'>
                                    <select
                                        className='filter-select'
                                        style={{ backgroundColor: 'var(--background-color)' }}
                                        value={selectedBdUser}
                                        onChange={(e) => setSelectedBdUser(e.target.value)}
                                    >
                                        <option value=''>Select User</option>
                                        {bdUsers.map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                            </div>
                        )}
                        {(viewType === 'attorney') && (
                            <>
                                <button className='action-micro quaternary' onClick={() => setHiddenValues(!hiddenValues)} style={{ backgroundColor: 'var(--background-color)' }}>
                                    {hiddenValues ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                <button className='action alt' onClick={() => setOpenFilters(prev => !prev)}>
                                    Filter
                                </button>
                            </>
                        )}
                    </div>

                    {viewType === 'bds' && initialEmployeeFetchComplete && (
                        <BDDashboard
                            user={user}
                            hideHeader
                            pageContainerClassName="task-view"
                            defaultViewType="summaries"
                            selectedBdUserOverride={selectedBdUser || null}
                            onSelectedBdUserOverride={setSelectedBdUser}
                            onUsersLoaded={setBdUsers}
                        />
                    )}
                    {viewType === 'bds' && !initialEmployeeFetchComplete && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '200px',
                                fontSize: '16px'
                            }}
                        >
                            Fetching data{loadingDots}
                        </div>
                    )}
                    {viewType === 'bd' && initialEmployeeFetchComplete && (
                        <BDDashboard
                            user={user}
                            hideHeader
                            pageContainerClassName="task-view"
                            selectedBdUserOverride={selectedBdUser || null}
                            onSelectedBdUserOverride={setSelectedBdUser}
                            onUsersLoaded={setBdUsers}
                        />
                    )}
                    {viewType === 'bd' && !initialEmployeeFetchComplete && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '200px',
                                fontSize: '16px'
                            }}
                        >
                            Fetching data{loadingDots}
                        </div>
                    )}
                    {viewType === 'dashboard' && (
                        <ModularDashboard
                            user={user}
                            dashboardId={selectedDashboardId}
                            mode='view'
                            onDashboardCreated={(dashboardItem, dashboards) => {
                                setDashboardNav(dashboards || []);
                                setSelectedDashboardId(dashboardItem?.id || null);
                                setViewType('dashboard');
                            }}
                            onDashboardUpdated={(dashboardItem, dashboards) => {
                                setDashboardNav(dashboards || []);
                                if (dashboardItem?.id) {
                                    setSelectedDashboardId(dashboardItem.id);
                                }
                            }}
                            onOpenLegacyView={(target) => {
                                if (target === 'legacy_attorney_dashboard') {
                                    setViewType('attorney');
                                    return;
                                }
                                if (target === 'legacy_bd_dashboard') {
                                    setViewType('bd');
                                }
                            }}
                        />
                    )}
                    {viewType === 'dashboard-create' && (
                        <ModularDashboard
                            user={user}
                            mode='create'
                            onDashboardCreated={(dashboardItem, dashboards) => {
                                setDashboardNav(dashboards || []);
                                setSelectedDashboardId(dashboardItem?.id || null);
                                setViewType('dashboard');
                            }}
                            onDashboardUpdated={(dashboardItem, dashboards) => {
                                setDashboardNav(dashboards || []);
                                if (dashboardItem?.id) {
                                    setSelectedDashboardId(dashboardItem.id);
                                }
                            }}
                            onOpenLegacyView={(target) => {
                                if (target === 'legacy_attorney_dashboard') {
                                    setViewType('attorney');
                                    return;
                                }
                                if (target === 'legacy_bd_dashboard') {
                                    setViewType('bd');
                                }
                            }}
                        />
                    )}
                    {viewType !== 'bd' && viewType !== 'bds' && viewType !== 'dashboard' && viewType !== 'dashboard-create' && (
                    <>
                    {openFilters && (
                        <div className='header-filters'>
                            {isGlobalAdmin && (
                                <div className='filter-group' style={{ flexDirection: 'row', gap: '15px' }}>
                                    <div className='select-wrapper'>
                                        <select className='filter-select' value={selectedAttorney ?? ''} onChange={(e) => setSelectedAttorney(e.target.value)}>
                                            <option value=''>Select an attorney</option>
                                            {attorneys.sort((a, b) => a.employee.localeCompare(b.employee)).map((attorney, index) => (
                                                <option key={index} value={attorney.user_id}>
                                                    {attorney.employee}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                    <div className='select-wrapper'>
                                        <select className='filter-select' value={selectedPractice ?? 0} onChange={(e) => setSelectedPractice(Number(e.target.value))}>
                                            <option value={0}>All</option>
                                            {practiceTypes
                                                .filter(practice => String(practice.name ?? '').toLowerCase() !== 'all' && Number(practice.id) !== 0)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((practice, index) => (
                                                <option key={index} value={Number(practice.id)}>
                                                    {practice.name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                    {screenWidth < 1200 && (
                                        <div className='collapse-data'>
                                            <ChevronDown size={16} onClick={() => setCollapsed(!collapsed)} style={{ cursor: 'pointer' }}/>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className='date-filters'>
                                <div className='select-wrapper' style={{ maxWidth: '250px' }}>
                                    <select 
                                        className='filter-select'
                                        value={selectedDateRange} 
                                        onChange={(e) => setSelectedDateRange(e.target.value)}
                                    >
                                        <option value='full_year'>Full Year</option>
                                        {[1, 2, 3, 4].map((quarter) => {
                                            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
                                            const labelPrefix = quarter >= currentQuarter ? "Last Year's " : '';
                                            return (
                                                <option key={quarter} value={`q${quarter}`}>{labelPrefix}Q{quarter}</option>
                                            );
                                        })}
                                        <option value='ytd'>Year to Date</option>
                                        <option value='30d'>Last 30 Days</option>
                                        <option value='90d'>Last 90 Days</option>
                                        <option value='6m'>Last 6 Months</option>
                                        <option value='1y'>Last Year (365 Days)</option>
                                        <option value='custom'>Custom Range</option>
                                    </select>
                                    <ChevronDown size={16} className="select-chevron" />
                                </div>
                                <div className='form-group nm small'>
                                    <label className='subtext'>Date From</label>
                                    <input
                                        type="date"
                                        value={dateFromFilter}
                                        onChange={(e) => {
                                            setDateFromFilter(e.target.value);
                                            setSelectedDateRange('custom');
                                        }}
                                        className='default-input'
                                    />
                                </div>
                                <div className='form-group nm small'>
                                    <label className='subtext'>Date To</label>
                                    <input
                                        type="date"
                                        value={dateToFilter}
                                        onChange={(e) => {
                                            setDateToFilter(e.target.value);
                                            setSelectedDateRange('custom');
                                        }}
                                        className='default-input'
                                    />
                                </div>
                                <button 
                                    className='action alt'
                                    onClick={() => {
                                        if (selectedDateRange === 'custom') {
                                            if (hasValidCustomDateRange) {
                                                fetchAttorneyData();
                                            }
                                            return;
                                        }

                                        const today = new Date();
                                        let startDate;
                                        let endDate = today;
                                        
                                        switch(selectedDateRange) {
                                            case 'full_year': {
                                                const range = getYearToDateRange(today);
                                                startDate = range.startDate;
                                                endDate = range.endDate;
                                                break;
                                            }
                                            case 'q1':
                                            case 'q2':
                                            case 'q3':
                                            case 'q4': {
                                                const range = getMostRecentCompletedQuarterRange(Number(selectedDateRange.slice(1)), today);
                                                startDate = range.startDate;
                                                endDate = range.endDate;
                                                break;
                                            }
                                            case 'ytd':
                                                startDate = new Date(today.getFullYear(), 0, 1);
                                                break;
                                            case '30d':
                                                startDate = new Date(today);
                                                startDate.setDate(today.getDate() - 30);
                                                break;
                                            case '90d':
                                                startDate = new Date(today);
                                                startDate.setDate(today.getDate() - 90);
                                                break;
                                            case '6m':
                                                startDate = new Date(today);
                                                startDate.setMonth(today.getMonth() - 6);
                                                break;
                                            case '1y':
                                                startDate = new Date(today);
                                                startDate.setFullYear(today.getFullYear() - 1);
                                                startDate.setDate(today.getDate() + 1);
                                                break;
                                            default:
                                                ({ startDate, endDate } = getYearToDateRange(today));
                                        }
                                        
                                        setDateFromFilter(formatLocalDateInputValue(startDate));
                                        setDateToFilter(formatLocalDateInputValue(endDate));
                                    }}
                                    disabled={selectedDateRange === 'custom' ? !hasValidCustomDateRange : false}
                                >
                                    Apply
                                </button>
                            </div>
                            {((isGlobalAdmin && selectedAttorney) || dateFromFilter || dateToFilter) && (
                                <button
                                    onClick={() => {
                                        if (isGlobalAdmin) {
                                            setSelectedAttorney(null);
                                            setSelectedPractice(0);
                                        }
                                        setDateFromFilter('');
                                        setDateToFilter('');
                                    }}
                                    className='action destroy'
                                    style={{ alignSelf: 'flex-end' }}
                                >
                                    Clear All Filters
                                </button>
                            )}
                        </div>
                    )}
                    
                    {/* Tasks View for Non-Attorney Users */}
                    {viewType === 'tasks' && tasksData && (
                        <div className='tasks-view'>
                            <div className='task-navigation-container'>
                                <MiniNav 
                                    options={['All Due Dates', 'On or Before Today', 'Due Today', 'Next 7 Days', 'Next 30 Days']} 
                                    selectedOption={['All Due Dates', 'On or Before Today', 'Due Today', 'Next 7 Days', 'Next 30 Days'].indexOf(taskOption)} 
                                    setSelectedOption={(val) => setSelectedTaskOption(['All Due Dates', 'On or Before Today', 'Due Today', 'Next 7 Days', 'Next 30 Days'][val])}
                                    icons={[CircleFadingArrowUp, CalendarClock, Clock11, Clock, Calendar]}
                                    notifications={getTaskNotifications()}
                                />
                                <button className='action tertiary' style={{ width: '100%' }} onClick={() => {
                                    setShowNewTaskModal(true);
                                    fetchUsers();
                                }}>Create Task</button>
                                <button className='action tertiary' style={{ width: '100%' }} onClick={() => {
                                    setShowNewNoteModal(true);
                                    fetchUsers();
                                }}>Create Note</button>
                                <Toggle label='List View' value={taskView} onChange={() => setTaskView(!taskView)} />
                                <Toggle label='Show Completed' value={showCompleted} onChange={() => setShowCompleted(!showCompleted)} />
                                <Toggle label='Assigned By Me' value={assignedByMe} onChange={() => setAssignedByMe(!assignedByMe)} />
                                {assignedByMe ? (
                                    paginatedTasks.assignedByMe > tasksPerPage && (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '10px' }}>
                                            <button 
                                                onClick={() => setAssignedTaskPage(prev => Math.max(1, prev - 1))}
                                                disabled={assignedTaskPage === 1}
                                                className='action alt'
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <span className='subtext'>
                                                {assignedTaskPage} of {Math.ceil(paginatedTasks.assignedByMe / tasksPerPage)}
                                            </span>
                                            <button 
                                                onClick={() => setAssignedTaskPage(prev => Math.min(Math.ceil(paginatedTasks.assignedByMe / tasksPerPage), prev + 1))}
                                                disabled={assignedTaskPage === Math.ceil(paginatedTasks.assignedByMe / tasksPerPage)}
                                                className='action'
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    )
                                ) : (
                                    paginatedTasks.tasks > tasksPerPage && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button 
                                                onClick={() => setTaskPage(prev => Math.max(1, prev - 1))}
                                                disabled={taskPage === 1}
                                                className='action alt'
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <span className='subtext'>
                                                {taskPage} of {Math.ceil(paginatedTasks.tasks / tasksPerPage)}
                                            </span>
                                            <button 
                                                onClick={() => setTaskPage(prev => Math.min(Math.ceil(paginatedTasks.tasks / tasksPerPage), prev + 1))}
                                                disabled={taskPage === Math.ceil(paginatedTasks.tasks / tasksPerPage)}
                                                className='action'
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                            {loadingTasks ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    minHeight: '300px',
                                    width: '100%',
                                    fontSize: '16px',
                                    color: 'var(--text-secondary)'
                                }}>
                                    Loading tasks{loadingDots}
                                </div>
                            ) : taskView === false ? (
                                <div className='tasks-list'>
                                    {filteredItems.length === 0 && (
                                        <div className='no-cases'>
                                            <div>No Tasks Found</div>
                                            <div className='subtext'>You don't have any tasks matching the selected filter.</div>
                                        </div>
                                    )}
                                    {filteredItems.map((task) => (
                                        <div 
                                            key={task.task_id} 
                                            className={`activity-feed-item feed-type-tasks ${task?.case_id ? 'has-case' : ''} ${task.is_completed ? 'completed' : ''} ${task.is_overdue ? 'overdue' : ''}`}
                                            onClick={(e) => handleTaskClick(task, e)}
                                            onDoubleClick={(e) => handleTaskDoubleClick(task, e)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="activity-feed-item-header subtext">
                                                <span className="activity-feed-item-author">
                                                    {task.author_user || task.author_name || 'Unknown'}
                                                </span>
                                                {task.case_name && (
                                                    <span className='activity-feed-item-case'>
                                                        Case: {task.case_name}
                                                        {task.client_name && ` - ${task.client_name}`}
                                                    </span>
                                                )}
                                                <span className="activity-feed-item-date subtext">
                                                    Due: {task.deadline_due ? new Date(task.deadline_due).toLocaleDateString() : 'No due date'}
                                                </span>
                                            </div>
                                            
                                            {task.subject && (
                                                <div className='activity-title'>
                                                    {task.subject}
                                                </div>
                                            )}
                                            
                                            {task.content && (
                                                <div className='activity-feed-item-content'>
                                                    <div 
                                                        dangerouslySetInnerHTML={{ __html: task.content }}
                                                        style={{
                                                            whiteSpace: "pre-wrap",
                                                            wordWrap: "break-word",
                                                            maxWidth: "100%",
                                                            overflowWrap: "break-word",
                                                            overflowX: "hidden",
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            
                                            <div className='activity-feed-item-type subtext'>
                                                {(() => {
                                                    const completionInfo = getTaskCompletionStatus(task, parseInt(userId));
                                                    if (task.is_overdue && completionInfo.status !== 'complete') {
                                                        return <span className='task-badge overdue'>Overdue</span>;
                                                    }
                                                    if (completionInfo.status === 'complete') {
                                                        return <span className='task-badge completed'>Completed</span>;
                                                    }
                                                    if (completionInfo.status === 'partial') {
                                                        return <span className='task-badge partial' title={`${completionInfo.completedCount} of ${completionInfo.totalCount} assignees completed`}>Partially Complete</span>;
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <table className='cases-table' style={{ justifyContent: 'flex-start' }}>
                                    <thead>
                                        <tr>
                                            <th 
                                                style={{ paddingLeft: '10px', width: '40%', justifyContent: 'flex-start', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => {
                                                    if (taskSortColumn === 'task') {
                                                        setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setTaskSortColumn('task');
                                                        setTaskSortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Task {taskSortColumn === 'task' && (taskSortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                style={{ width: '10%', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => {
                                                    if (taskSortColumn === 'status') {
                                                        setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setTaskSortColumn('status');
                                                        setTaskSortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Status {taskSortColumn === 'status' && (taskSortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                style={{ width: '10%', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => {
                                                    if (taskSortColumn === 'due') {
                                                        setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setTaskSortColumn('due');
                                                        setTaskSortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Due {taskSortColumn === 'due' && (taskSortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                style={{ width: '20%', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => {
                                                    if (taskSortColumn === 'case') {
                                                        setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setTaskSortColumn('case');
                                                        setTaskSortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Case/Lead {taskSortColumn === 'case' && (taskSortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th 
                                                style={{ width: '20%', cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => {
                                                    if (taskSortColumn === 'assigned') {
                                                        setTaskSortDirection(taskSortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setTaskSortColumn('assigned');
                                                        setTaskSortDirection('asc');
                                                    }
                                                }}
                                            >
                                                {assignedByMe ? 'Assignee' : 'Assigned By'} {taskSortColumn === 'assigned' && (taskSortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredItems.map((task) => {
                                            const completionInfo = getTaskCompletionStatus(task, parseInt(userId));
                                            const isCreator = parseInt(task.author_id) === parseInt(userId);
                                            // Use server-provided data if available, otherwise calculate
                                            const isAssignee = task.is_assignee !== undefined ? task.is_assignee : completionInfo.isAssignee;
                                            const userCompleted = task.user_completed !== undefined ? task.user_completed : completionInfo.userCompleted;
                                            const canToggle = isAssignee || isCreator;
                                            // If user is an assignee, use their personal status; if creator only, use overall status
                                            const currentCompletionStatus = isAssignee ? userCompleted : (completionInfo.status === 'complete');
                                            
                                            return (
                                                <tr 
                                                    key={task.task_id}
                                                    onClick={(e) => handleTaskClick(task, e)}
                                                    onDoubleClick={(e) => handleTaskDoubleClick(task, e)}
                                                    onMouseEnter={() => {
                                                        if (hoverTimeoutRef.current) {
                                                            clearTimeout(hoverTimeoutRef.current);
                                                            hoverTimeoutRef.current = null;
                                                        }
                                                        setHoveredTaskRow(task.task_id);
                                                        setFadingOutTaskRow(null);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredTaskRow(null);
                                                        setFadingOutTaskRow(task.task_id);
                                                        hoverTimeoutRef.current = setTimeout(() => {
                                                            setFadingOutTaskRow(null);
                                                        }, 300);
                                                    }}
                                                    className='case'
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px', paddingLeft: '10px', width: '40%', borderRight: '1px solid var(--border-color)', position: 'relative' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, overflow: 'hidden', height: '100%' }}>
                                                            {task?.subject && <span style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '14px', lineHeight: '1', maxWidth: '65%', whiteSpace: 'nowrap', overflowX: 'hidden', minHeight: '100%', 
                                                            textOverflow: 'ellipsis', flexShrink: 0 }}>{task.subject}</span>}<span className='subtext' style={{ whiteSpace: 'pre-wrap', overflow: 'hidden' }}>{(() => {
                                                                const content = String(task.content);
                                                                const newlineIndex = content.indexOf('\n');
                                                                const maxLength = 100;
                                                                
                                                                if (newlineIndex !== -1 && newlineIndex < maxLength) {
                                                                    return `${content.slice(0, newlineIndex)}...`;
                                                                } else if (content.length > maxLength) {
                                                                    return `${content.slice(0, maxLength)}...`;
                                                                }
                                                                return content;
                                                            })()}</span>
                                                        </div>
                                                        {(hoveredTaskRow === task.task_id || fadingOutTaskRow === task.task_id) && canToggle && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                right: 0,
                                                                top: 0,
                                                                bottom: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                paddingRight: '10px',
                                                                paddingLeft: '30px',
                                                                background: 'linear-gradient(to right, transparent -20%, transparent -00%, var(--secondary-color) 100%)',
                                                                opacity: hoveredTaskRow === task.task_id ? 1 : 0,
                                                                transition: 'opacity 0.3s ease-in-out',
                                                                zIndex: 1,
                                                            }}>
                                                                <button 
                                                                    className='action tertiary hover-effect'
                                                                    style={{ pointerEvents: 'auto', fontSize: '12px', padding: '4px 8px' }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleTaskCompletion(task.task_id, currentCompletionStatus);
                                                                    }}
                                                                    title={currentCompletionStatus ? 'Mark incomplete' : 'Mark complete'}
                                                                >
                                                                    {currentCompletionStatus ? 'Mark Incomplete' : 'Mark Done'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '10%' }}>
                                                    {(() => {
                                                        const completionInfo = getTaskCompletionStatus(task, parseInt(userId));
                                                        const statusClass = completionInfo.status === 'complete' ? 'completed' :
                                                                          completionInfo.status === 'partial' ? 'partial' :
                                                                          task.is_overdue ? 'overdue' : 'incomplete';
                                                        const statusText = completionInfo.status === 'complete' ? 'Completed' :
                                                                         completionInfo.status === 'partial' ? 'Partial' :
                                                                         task.is_overdue ? 'Overdue' : 'Incomplete';
                                                        const title = completionInfo.status === 'partial' ? 
                                                            `${completionInfo.completedCount} of ${completionInfo.totalCount} assignees completed` : '';
                                                        
                                                        return (
                                                            <span className={`task-status-badge ${statusClass}`} title={title}>
                                                                {statusText}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '10%' }}>
                                                    {new Date(task.deadline_due).toLocaleDateString()}
                                                </td>
                                                <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20%' }}>
                                                    {task.case_name ? task.case_name : task.lead_name ? task.lead_name : ''}&nbsp;{(task?.case_name || task?.lead_name) && <span className='subtext'>({task.case_name ? 'case' : task.lead_name ? 'lead' : ''})</span>}
                                                </td>
                                                {assignedByMe ? (
                                                    <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20%' }}>
                                                        {task?.assigned_to_name?.[0]}{task?.assigned_to_user?.length > 0 ? <span className='subtext'>&nbsp;(@{task?.assigned_to_user?.[0]})</span> : ''}{task?.assigned_to_name?.length > 1 ? <span className='subtext'>&nbsp;+{task?.assigned_to_name?.length - 1} more</span> : ''}
                                                    </td>
                                                ) : (
                                                    <td style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20%' }}>
                                                        {task?.author_name}&nbsp;{task?.author_user && <span className='subtext'>(@{task?.author_user})</span>}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                    
                    {/* My Cases Section - Only show in tasks view */}
                    {viewType === 'tasks' && (
                        <div className='my-cases-section'>
                            <div className='my-cases-header'>
                                <h2>My Cases</h2>
                                {myCases.length > casesPerPage && !listView && (
                                <div>
                                    <button 
                                        onClick={prevCases}
                                        disabled={currentPage === 1}
                                        className='action alt'
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span>
                                        {currentPage} of {totalPages}
                                    </span>
                                    <button 
                                        onClick={nextCases}
                                        disabled={currentPage === totalPages}
                                        className='action'
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Filter Controls for My Cases */}
                        {myCases.length > 0 && (
                            <div className='cases-filter'>
                                <div className='cases-filter-container'>
                                    <div className='form-group nm small'>
                                        <label className='subtext'>
                                            Search Cases & Clients
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search by case name or client name..."
                                            value={nameFilter}
                                            onChange={(e) => setNameFilter(e.target.value)}
                                        />
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>
                                            Settlement Status
                                        </label>
                                        <div className='select-wrapper'>
                                            <select
                                                className='filter-select'
                                                value={settlementFilter}
                                                onChange={(e) => setSettlementFilter(e.target.value)}
                                            >
                                                <option value="all">All Cases</option>
                                                <option value="settled">Settled</option>
                                                <option value="unsettled">Unsettled</option>
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>
                                            My Role
                                        </label>
                                        <div className='select-wrapper'>
                                            <select
                                                value={roleFilter}
                                                onChange={(e) => setRoleFilter(e.target.value)}
                                                className='filter-select'
                                            >
                                                <option value="all">All Roles</option>
                                                <option value="Primary">Primary</option>
                                                <option value="Attorney">Attorney</option>
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>
                                            Phase
                                        </label>
                                        <div className='select-wrapper'>
                                            <select
                                                value={casePhaseFilter}
                                                onChange={(e) => setCasePhaseFilter(e.target.value)}
                                                className='filter-select'
                                            >
                                                <option value="all">All Phases</option>
                                                {availableTaskCasePhaseOptions.map((phase) => (
                                                    <option key={phase.id} value={phase.id}>
                                                        {phase.phase}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>
                                            First Primary
                                        </label>
                                        <div className='select-wrapper'>
                                            <select
                                                value={firstPrimaryFilter}
                                                onChange={(e) => setFirstPrimaryFilter(e.target.value)}
                                                className='filter-select'
                                            >
                                                <option value="all">All Primaries</option>
                                                {availableTaskCasePrimaryOptions.map((primaryUser) => (
                                                    <option key={primaryUser.id} value={primaryUser.id}>
                                                        {primaryUser.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>
                                            Case Type
                                        </label>
                                        <div className='select-wrapper'>
                                            <select
                                                value={caseTypeFilter}
                                                onChange={(e) => setCaseTypeFilter(e.target.value)}
                                                className='filter-select'
                                            >
                                                <option value="all">All Case Types</option>
                                                {availableTaskCaseTypeOptions.map((practice) => (
                                                    <option key={practice.id} value={practice.id}>
                                                        {practice.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    <div className='checkbox-group-cont' style={{ height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Checkbox
                                            label="Show Archived"
                                            value={showArchivedCases}
                                            onChange={() => setShowArchivedCases(!showArchivedCases)}
                                        />
                                    </div>
                                    <Toggle label='List View' value={listView} onChange={() => setListView(!listView)} />
                                    <button 
                                        className='action tertiary' 
                                        style={{ borderColor: 'var(--fill)', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        onClick={exportCasesToCSV}
                                        title={`Export ${filteredCasesForDisplay.length} case${filteredCasesForDisplay.length !== 1 ? 's' : ''}`}
                                    >
                                        <Download size={16} />
                                        Export ({filteredCasesForDisplay.length})
                                    </button>
                                </div>

                                <div className='subtext cases-count'>
                                    {filteredCasesForDisplay.length} of {myCases.length} cases
                                </div>
                            </div>
                        )}

                        {loadingCases ? (
                            <div className='cases-loading'>
                                Loading your cases...
                            </div>
                        ) : listView ? (
                            <table className='cases-table'>
                                <thead>
                                    <tr>
                                        <th className='case-name' style={{ paddingLeft: '5px' }}>Case Name</th>
                                        <th style={{ width: '20%' }}>Phase</th>
                                        <th style={{ width: '20%' }}>Role</th>
                                        <th style={{ width: '10%' }}>Created</th>
                                        <th style={{ width: '10%' }}>Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCasesForDisplay.map((item) => (
                                        <tr 
                                            key={item.case_id}
                                            onClick={() => {
                                                openCaseInNewTab(item.case_id);
                                            }}
                                            className='case'
                                        >
                                            <td className='case-name'>
                                                <a 
                                                    href={`/case/${item?.case_id}`} 
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    onClick={(e) => e.stopPropagation()}
                                                    className='case-name-link'
                                                    style={{ width: '100%' }}
                                                >
                                                    <div className="case-name-content">
                                                        {item?.contact?.contact_display?.includes('uploads')
                                                            ? <img className='contact-initials' src={`https://api.casedb.co/${item?.contact?.contact_display}`} alt="Profile" />
                                                            : <span className={getContactInitialsClassName(item?.case_id)}>{item?.contact?.contact_display || item?.case_name?.charAt(0) || ''}</span>
                                                        }
                                                    </div>
                                                    {item?.case_name}
                                                    {item?.contact_language && item?.contact_language.toLowerCase() !== 'english' && (
                                                        <Languages 
                                                            size={16} 
                                                            className="language-indicator" 
                                                            title={`Needs an interpreter for ${item?.contact_language}`}
                                                        />
                                                    )}
                                                    {screenWidth >= 768 &&
                                                        <>
                                                            {JSON.parse(item?.tags)?.slice(0, 2)?.map((value) => (
                                                                <span className='tag caps' key={value}>
                                                                    {value.includes('#') ? value : `#${value}`}
                                                                </span>
                                                            ))}
                                                            {JSON.parse(item?.tags)?.length > 2 && (
                                                                <span className='tag more-tags caps' title={JSON.parse(item?.tags)?.slice(2).join(', ')}>
                                                                    +{JSON.parse(item?.tags)?.length - 2} more
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                </a>
                                            </td>
                                            <td className='default small-text' style={{ width: '20%' }}>
                                                {item.phase_name || 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '20%' }}>
                                                {item.role_names[0] || 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '10%' }}>
                                                {item.create_date ? formatLastModified(item.create_date)[3] : 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '10%' }}>
                                                {item.updated_at ? formatLastModified(item.updated_at)[5] : 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : myCases.length > 0 && filteredCasesForDisplay.length > 0 ? (
                            <div 
                                className='cases-grid'
                                style={{
                                    gridTemplateColumns: visibleItems.length === 1
                                        ? 'minmax(0, 1fr)'
                                        : `repeat(${Math.min(visibleItems.length, 5)}, minmax(0, 1fr))`,
                                    maxWidth: visibleItems.length === 1 ? '20%' : '100%'
                                }}
                            >
                                {visibleItems.map((item, index) => (
                                    <div 
                                        key={item.case_id} 
                                        className='case-card'
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                        }}
                                        onClick={() => {
                                            openCaseInNewTab(item.case_id);
                                        }}
                                    >
                                        <div className='case-card-content'>
                                            <h4>
                                                {item.case_name}
                                            </h4>
                                            <div className='case-card-subtitle'>
                                                Case #{item.case_id} • {item.type_name || 'Unknown Type'}
                                            </div>
                                        </div>
                                        
                                        {item.full_name && (
                                            <div className='case-card-client'>
                                                <strong>Client:</strong> {item.contact?.full_name || item.full_name}
                                            </div>
                                        )}
                                        
                                        {item.role_names && Array.isArray(item.role_names) && item.role_names.length > 0 && (
                                            <div className='case-card-roles'>
                                                <strong>My Roles:</strong> 
                                                <div className='case-card-role-list'>
                                                    {item?.role_names?.map((role, idx) => (
                                                        <span key={idx}>
                                                            {role}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {item.settlement_amount > 0 && (
                                            <div className='case-card-settlement'>
                                                <strong>Settlement:</strong> {formatValue(item.settlement_amount, 'currency')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : myCases.length > 0 ? (
                            <div className='no-cases'>
                                <div>No Cases Match Your Filters</div>
                                <div className='subtext'>
                                    Try adjusting your search criteria or clearing the filters above.
                                </div>
                            </div>
                        ) : (
                            <div className='no-cases'>
                                <div>No Cases Found</div>
                                <div className='subtext'>
                                    You are not assigned as the primary person for any roles in any cases.
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                    
                    {/* Attorney View */}
                    {viewType === 'attorney' && attorneyData && (
                        <div className='dashboard-specific-content'>
                            <div className='homepage-content'>
                                <div className='data one-onefive'>
                                    <div className='data-label'>
                                        <span>Final Settlement</span>
                                        {/*<span className='data-expand'><ArrowUpRight size={16} /></span>*/}
                                    </div>
                                    <span className='data-value'>{formatValue(attorneyData.totals.total_final_settlement, 'currency')}</span>
                                    <span className='data-change subtext'>Stored in the Final Settlement field.</span>
                                </div>
                                <div className='data one-onefive'>
                                    <div className='data-label'>
                                        <span>Attorney Pay</span>
                                    </div>
                                    <span className='data-value'>
                                        {maskCurrency(attorneyData.totals.total_salary_prorated + attorneyData.totals.total_bonus)}
                                    </span>
                                    <span className='data-change subtext'>
                                        {maskCurrency(attorneyData?.totals.total_salary_prorated)} Salary + {maskCurrency(attorneyData?.totals.total_bonus)} Discretionary Bonuses
                                    </span>
                                </div>
                                <div className='data one-onefive'>
                                    <div className='data-label'>
                                        <span>DB Origination</span>
                                    </div>
                                    <span className='data-value'>
                                        {formatValue(attorneyData?.totals?.total_db_origination_count ? attorneyData?.totals?.total_db_origination_count : 0, 'number')}
                                    </span>
                                    <span className='data-change subtext'>
                                        {attorneyData?.employees?.filter(emp => emp.db_origination_count > 0).length} employees contributed to count.
                                    </span>
                                </div>
                                <div className={`data one-onefive main ${attorneyData?.percent_delta > 0 ? 'up' : attorneyData?.percent_delta < 0 ? 'down' : 'stagnant'}`}>
                                    <div className='data-label'>
                                        <span>Total Due to Firm</span>
                                    </div>
                                    <span className='data-value'>
                                        {formatValue(attorneyData?.totals?.total_net_attorney_fees ?? attorneyData?.totals?.net_attorney_fees ?? 0, 'currency')}
                                        {/*<span className='data-subvalue'>({maskCurrency(attorneyData?.earned_to_date ?? attorneyData.salary_to_date)} Salary YTD)</span>*/}
                                    </span>
                                    <span className='data-change'>
                                        Net attorney fees.
                                    </span>
                                </div>
                                {!dashboardCollapsed && (
                                    <>
                                        <div className='data one-onefive'>
                                            <div className='data-label'>
                                                <span>Settlement Retention</span>
                                            </div>
                                            <span className='data-value'>
                                                {(() => {
                                                    const toNumber = (value) => {
                                                        const normalized = Number(String(value ?? 0).replace(/[^0-9.-]/g, ''));
                                                        return Number.isFinite(normalized) ? normalized : 0;
                                                    };

                                                    const totalSettlements = toNumber(
                                                        attorneyData?.totals?.total_final_settlement
                                                        ?? attorneyData?.totals?.final_settlement
                                                    );
                                                    const totalNetAttorneyFees = toNumber(
                                                        attorneyData?.totals?.total_net_attorney_fees
                                                        ?? attorneyData?.totals?.net_attorney_fees
                                                    );
                                                    
                                                    if (totalSettlements === 0) {
                                                        return 'N/A';
                                                    }
                                                    
                                                    const retentionPercent = (totalNetAttorneyFees / totalSettlements) * 100;
                                                    return `${retentionPercent.toFixed(1)}%`;
                                                })()}
                                            </span>
                                            <span className='data-change subtext'>
                                                Net Attorney Fees total / Final Settlement total × 100.
                                            </span>
                                        </div>
                                        <div className='data one-onefive double-wide'>
                                            <div className='data-label'>
                                                <span>Settlement / Salary</span>
                                                <span className='data-subvalue'>Goal: 4x Salary</span>
                                            </div>
                                            <div className='progress-bar-container'>
                                                {(() => {
                                                    // Check if date range is a full year (1/1 to 12/31)
                                                    const isFullYear = dateFromFilter && dateToFilter && 
                                                        dateFromFilter.endsWith('-01-01') && dateToFilter.endsWith('-12-31') &&
                                                        dateFromFilter.substring(0, 4) === dateToFilter.substring(0, 4);
                                                    
                                                    const salaryForGoal = !isFullYear ? attorneyData?.totals?.total_salary_prorated : attorneyData?.totals?.total_salary_annual;
                                                    const goalAmount = salaryForGoal * 4;
                                                    const totalDueToFirm = attorneyData?.totals?.total_net_attorney_fees ?? attorneyData?.totals?.net_attorney_fees ?? 0;
                                                    const progressPercent = goalAmount > 0 ? (totalDueToFirm / goalAmount) * 100 : 0;
                                                    
                                                    return (
                                                        <>
                                                            <div 
                                                                className='progress-bar-cont' 
                                                                style={{ 
                                                                    width: `${Math.min(progressPercent, 100)}%`,
                                                                    backgroundColor: progressPercent >= 100 
                                                                        ? '#4ade80' 
                                                                        : progressPercent >= 75 
                                                                            ? '#43a0e1ff' 
                                                                            : '#f87171'
                                                                }}
                                                            />
                                                            <span
                                                                className='progress-bar-text'
                                                                style={{
                                                                    color: progressPercent >= 75
                                                                        ? '#000'
                                                                        : '#fff'
                                                                }}
                                                            >
                                                                {formatValue(totalDueToFirm, 'currency')} / {hiddenValues ? '$#,###.##' : formatValue(goalAmount, 'currency')}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <span className='data-change subtext'>
                                                {(() => {
                                                    const isFullYear = dateFromFilter && dateToFilter && 
                                                        dateFromFilter.endsWith('-01-01') && dateToFilter.endsWith('-12-31') &&
                                                        dateFromFilter.substring(0, 4) === dateToFilter.substring(0, 4);
                                                    
                                                    const salaryForGoal = !isFullYear ? attorneyData?.totals?.total_salary_prorated : attorneyData?.totals?.total_salary_annual;
                                                    const goalAmount = salaryForGoal * 4;
                                                    const totalDueToFirm = attorneyData?.totals?.total_net_attorney_fees ?? attorneyData?.totals?.net_attorney_fees ?? 0;
                                                    const progressPercent = goalAmount > 0 ? (totalDueToFirm / goalAmount) * 100 : 0;
                                                    
                                                    return `${progressPercent.toFixed(1)}% of goal | ${hiddenValues ? '$#,###.##' : formatValue(salaryForGoal, 'currency')} × 4 = ${hiddenValues ? '$#,###.##' : formatValue(goalAmount, 'currency')}`;
                                                })()}
                                            </span>
                                        </div>
                                        <div className='data one-onefive'>
                                            <div className='data-label'>
                                                <span>Attorney Docket <span className='subtext'>(open during period / closed / open on {formatDate(dateToFilter)[0]})</span></span>
                                                <a href='reports?id=98' target='_blank'><span className='data-expand'><ArrowUpRight size={16} /></span></a>
                                            </div>
                                            {(selectedAttorney && Number(attorneyData?.filters?.user_id) > 0) ? (
                                                <>
                                                    <span className='data-value horizontal bar small-text' style={{ width: '100%'}}>{formatValue(attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened)}</span>
                                                    <span className='data-value horizontal bar small-text' style={{ minWidth: `${((attorneyData?.totals?.total_cases_closed / (attorneyData?.totals?.total_cases_opened + attorneyData?.totals?.total_cases_open_start)) * 100)}%`, justifyContent: attorneyData?.totals?.total_cases_closed == 0 ? 'center' : '' }}>{formatValue(attorneyData?.totals?.total_cases_closed ?? 0)}</span>
                                                    <span className='data-value horizontal bar small-text' style={{ minWidth: `${((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed) / (attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) * 100}%`, justifyContent: ((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed) == 0 ? 'center' : '' }}>{formatValue((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed)}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className='data-value horizontal bar small-text' style={{ width: '100%'}}>{formatValue(attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened)}</span>
                                                    <span className='data-value horizontal bar small-text' style={{ minWidth: `${((attorneyData?.totals?.total_cases_closed / (attorneyData?.totals?.total_cases_opened + attorneyData?.totals?.total_cases_open_start)) * 100)}%`, justifyContent: attorneyData?.totals?.total_cases_closed == 0 ? 'center' : '' }}>{formatValue(attorneyData?.totals?.total_cases_closed ?? 0)}</span>
                                                    <span className='data-value horizontal bar small-text' style={{ minWidth: `${((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed) / (attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) * 100}%`, justifyContent: ((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed) == 0 ? 'center' : '' }}>{formatValue((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed)}</span>
                                                </>
                                            )}

                                        </div>
                                        {/*<div className='data one-onefive'>
                                            {attorneyData?.qb_expenses ? (
                                                <>
                                                    <div className='data-label'>
                                                        <span>Firm Expenses</span>
                                                    </div>
                                                    <span className='data-value'>
                                                        {maskCurrency(attorneyData?.qb_expenses)}
                                                    </span>
                                                    <span className='data-change subtext'>
                                                        {formatValue(attorneyData?.qb_expense_count, 'int')} transactions recorded in QuickBooks
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className='data-label'>
                                                        <span>Case Expenses <span style={{ fontSize: '12px'}}>(from {attorneyData?.cases_with_expenses_count} cases)</span></span>
                                                        <a href='reports?id=97' target='_blank'><span className='data-expand'><ArrowUpRight size={16} /></span></a>
                                                    </div>
                                                    <span className='data-value'>
                                                        {formatValue(attorneyData?.qb_expenses ?? attorneyData?.total_expenses, 'currency')}
                                                    </span>
                                                    <span className='data-change subtext'>
                                                        {((attorneyData?.total_expenses / attorneyData?.current_case_valuation) * 100).toFixed(2)}% of total Case Valuation
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {/*<div className='data one-onefive'>
                                            <div className='data-label'>
                                                <span>Case Valuation <span style={{ fontSize: '12px'}}>(after splits)</span></span>
                                            </div>
                                            <span className='data-value'>
                                                {formatValue(attorneyData?.adjusted_case_valuation, 'currency')}
                                            </span>
                                            <span className='data-change subtext'>
                                                {Math.abs(attorneyData?.adjusted_vs_projected_percent)}% projected {attorneyData?.adjusted_vs_projected_percent > 0 ? 'more' : 'less'} than settled total
                                            </span>
                                        </div>
                                        <div className='data one-onefive'>
                                            <div className='data-label'>
                                                <span>Case Valuation <span style={{ fontSize: '12px'}}>(before splits)</span></span>
                                                <a href='reports?id=96' target='_blank'><span className='data-expand'><ArrowUpRight size={16} /></span></a>
                                            </div>
                                            <span className='data-value'>
                                                {formatValue(attorneyData?.current_case_valuation, 'currency')}
                                            </span>
                                            <span className='data-change subtext'>
                                                Based on Projected Settlement value from unsettled cases.
                                            </span>
                                        </div>*/}
                                        <div className='data four-one'>
                                            <div className='data-splitscreen'>
                                                <span className='data-value'>
                                                    {formatValue(attorneyData?.totals?.total_cases_open_start, 'number')}
                                                </span>
                                                <span className='data-change subtext'>
                                                    Open Cases at Start of Period
                                                </span>
                                            </div>
                                            <span className='data-to-data'>
                                                <MoveRight size={18} />
                                            </span>
                                            <div className='data-splitscreen'>
                                                <span className='data-value'>
                                                    {formatValue((attorneyData?.totals?.total_cases_open_start + attorneyData?.totals?.total_cases_opened) - attorneyData?.totals?.total_cases_closed)}
                                                </span>
                                                <span className='data-change subtext'>
                                                    Open Cases at End of Period
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {/*<div className='data one-onefive' style={{ rowSpan: 6 }}>
                                    <div className='data-label'>
                                        <span>Referral List</span>
                                        <span className='data-expand'><ArrowUpRight size={16} /></span>
                                    </div>
                                    <div className='data-value small-text'>
                                        <ul>
                                            {attorneyData?.by_referred_by?.filter(referral => referral.source != "").slice(0, 5)?.map(referral => (
                                                <li key={referral.source}>{referral.source}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>*/}
                                <div 
                                    className='homepage-collapse-toggle' 
                                    onClick={() => setDashboardCollapsed(!dashboardCollapsed)}
                                    title={dashboardCollapsed ? 'Expand Dashboard' : 'Collapse Dashboard'}
                                >
                                    {dashboardCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                </div>
                            </div>
                            {attorneyData?.employees?.length > 0 && 
                                <div className='employee-info'>
                                    {attorneyData?.employees?.filter(emp => Number(emp.shareholder) === 1).length > 0 && (
                                        <>
                                            <div className='employee-info-header' style={{ padding: '8px 16px' }}>
                                                <div className='table-title' style={{ height: '28px' }}>Shareholders</div>
                                            </div>
                                            <table className='employee-data-table shareholder-table' style={{ borderBottom: attorneyData?.employees?.filter(emp => Number(emp.shareholder) === 0).length === 0 ? 'none' : '1px solid var(--border-color)' }}>
                                                <thead className='subtext'>
                                                    <tr>
                                                        <th>
                                                            Lawyer
                                                        </th>
                                                        <th>
                                                            Salary
                                                        </th>
                                                        <th>
                                                            Bonus
                                                        </th>
                                                        <th>
                                                            Origination
                                                        </th>
                                                        <th>
                                                            Fees
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className='small-text'>
                                                    {attorneyData?.employees?.sort((a, b) => a.employee.localeCompare(b.employee)).filter(emp => Number(emp.shareholder) === 1)?.map((emp, index) => (
                                                        <tr key={index} className={emp.name === attorneyData.employee ? 'current-attorney' : ''}>
                                                            <td>
                                                                {emp?.employee}
                                                            </td>
                                                            <td>
                                                                {maskCurrency(emp?.salary_prorated)} / {maskCurrency(emp?.salary_annual)}
                                                            </td>
                                                            <td>
                                                                {maskCurrency(emp?.bonus)}
                                                            </td>
                                                            <td>
                                                                {formatValue(emp?.db_origination_count)} 
                                                            </td>
                                                            <td>
                                                                {formatValue(emp?.net_attorney_fees, 'currency')} / {formatValue(emp?.final_settlement, 'currency')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                    {attorneyData?.employees?.filter(emp => Number(emp.shareholder) === 0).length > 0 && (
                                        <>
                                            <div className='employee-info-header' style={{ padding: '8px 16px' }}>
                                                <div className='table-title' style={{ height: '28px' }}>Non-Shareholders</div>
                                            </div>
                                            <table className='employee-data-table non-shareholder-table'>
                                                <thead className='subtext'>
                                                    <tr>
                                                        <th>
                                                            Lawyer
                                                        </th>
                                                        <th>
                                                            Salary
                                                        </th>
                                                        <th>
                                                            Bonus
                                                        </th>
                                                        <th>
                                                            Origination
                                                        </th>
                                                        <th>
                                                            Fees
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className='small-text'>
                                                    {attorneyData?.employees?.sort((a, b) => a.employee.localeCompare(b.employee)).filter(emp => Number(emp.shareholder) === 0 && emp.user_id !== 6)?.map((emp, index) => (
                                                        <tr key={index} className={emp.name === attorneyData.employee ? 'current-attorney' : ''}>
                                                            <td>
                                                                {emp?.employee}
                                                            </td>
                                                            <td>
                                                                {maskCurrency(emp?.salary_prorated)} / {maskCurrency(emp?.salary_annual)}
                                                            </td>
                                                            <td>
                                                                {maskCurrency(emp?.bonus)}
                                                            </td>
                                                            <td>
                                                                {formatValue(emp?.db_origination_count)} 
                                                            </td>
                                                            <td>
                                                                {formatValue(emp?.net_attorney_fees, 'currency')} / {formatValue(emp?.final_settlement, 'currency')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </div>
                            }
                        </div>
                    )}

                    {/* Settlements Section - Only show in attorney view */}
                    {viewType === 'attorney' && (
                        <div className='my-cases-section'>
                            <div className='my-cases-header'>
                                <div className='settlement-header' style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <h2>Settlements</h2>
                                    <span className='subtext'>Settlements (not partial payments) finalized within the selected date range.</span>
                                </div>
                                {filteredCasesForDisplay.length > casesPerPage && (
                                <div>
                                    <button 
                                        onClick={prevCases}
                                        disabled={currentPage === 1}
                                        className='action alt'
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span>
                                        {currentPage} of {totalPages}
                                    </span>
                                    <button 
                                        onClick={nextCases}
                                        disabled={currentPage === totalPages}
                                        className='action'
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Filter Controls */}
                        {displayData.length > 0 && (
                            <div className='cases-filter'>
                                <div className='cases-filter-container'>
                                    <div className='form-group nm small'>
                                        <label className='subtext'>
                                            Search Settlements
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search by case name, client, or attorney..."
                                            value={nameFilter}
                                            onChange={(e) => setNameFilter(e.target.value)}
                                        />
                                    </div>
                                    <Toggle label='Table View' value={settlementListView} onChange={() => setSettlementListView(!settlementListView)} />
                                    <button 
                                        className='action tertiary' 
                                        style={{ borderColor: 'var(--fill)', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        onClick={exportCasesToCSV}
                                        title={`Export ${filteredCasesForDisplay.length} settlement${filteredCasesForDisplay.length !== 1 ? 's' : ''}`}
                                    >
                                        <Download size={16} />
                                        Export ({filteredCasesForDisplay.length})
                                    </button>
                                </div>

                                <div className='subtext cases-count'>
                                    {filteredCasesForDisplay.length} of {displayData.length} settlements
                                </div>
                            </div>
                        )}

                        {loadingEmployeeData ? (
                            <div className='cases-loading'>
                                Loading settlements...
                            </div>
                        ) : settlementListView ? (
                            <table className='cases-table'>
                                <thead>
                                    <tr>
                                        <th style={{ paddingLeft: '5px' }} className='case-name'>Case Name</th>
                                        <th style={{ width: '20%' }}>Client</th>
                                        <th style={{ width: '20%' }}>Attorney</th>
                                        <th style={{ width: '14%' }}>Settlement Date</th>
                                        <th style={{ width: '13%', textAlign: 'right' }}>Amount</th>
                                        <th style={{ width: '13%', textAlign: 'right', paddingRight: '5px' }}>Net Attorney Fees</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCasesForDisplay.map((item) => (
                                        <tr 
                                            key={item.case_id}
                                            onClick={() => {
                                                openCaseInNewTab(item.case_id);
                                            }}
                                            className='case'
                                        >
                                            <td className='case-name'>
                                                <a 
                                                    href={`/case/${item?.case_id}`} 
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    onClick={(e) => e.stopPropagation()}
                                                    className='case-name-link'
                                                    style={{ width: '100%' }}
                                                >
                                                    <div className="case-name-content">
                                                        <span className={getContactInitialsClassName(item?.case_id)}>
                                                            {item?.client_name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || item?.case_name?.charAt(0) || '?'}
                                                        </span>
                                                    </div>
                                                    {item?.case_name}
                                                </a>
                                            </td>
                                            <td className='default small-text' style={{ width: '20%' }}>
                                                {item.client_name || 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '20%' }}>
                                                {item.attorney_name || 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '14%' }}>
                                                {item.settlement_date ? formatDatePreservingDay(item.settlement_date) : 'N/A'}
                                            </td>
                                            <td className='default small-text' style={{ width: '13%', textAlign: 'right' }}>
                                                {formatValue(item.settlement_amount, 'currency')}
                                            </td>
                                            <td className='default small-text' style={{ width: '13%', textAlign: 'right', paddingRight: '5px' }}>
                                                {formatValue(item.net_attorney_fees, 'currency')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : displayData.length > 0 && filteredCasesForDisplay.length > 0 ? (
                            <div 
                                className='cases-grid'
                                style={{
                                    gridTemplateColumns: visibleItems.length === 1
                                        ? 'minmax(0, 1fr)'
                                        : `repeat(${Math.min(visibleItems.length, 5)}, minmax(0, 1fr))`,
                                    maxWidth: visibleItems.length === 1 ? '20%' : '100%'
                                }}
                            >
                                {visibleItems.map((item, index) => (
                                    <div 
                                        key={item.case_id} 
                                        className='case-card settlement-card'
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                        }}
                                        onClick={() => {
                                            openCaseInNewTab(item.case_id);
                                        }}
                                    >
                                        <div className='case-card-content'>
                                            <h4>
                                                {item.case_name}
                                            </h4>
                                            <div className='case-card-subtitle'>
                                                Case #{item.case_id} • Settled {item.settlement_date ? formatDatePreservingDay(item.settlement_date) : 'N/A'}
                                            </div>
                                        </div>
                                        
                                        {item.client_name && (
                                            <div className='case-card-client'>
                                                <strong>Client:</strong> {item.client_name}
                                            </div>
                                        )}
                                        
                                        {item.attorney_name && (
                                            <div className='case-card-attorney'>
                                                <strong>Attorney:</strong> {item.attorney_name}
                                            </div>
                                        )}
                                        
                                        {item.settlement_amount > 0 && (
                                            <div className='case-card-settlement highlight'>
                                                <strong>Settlement:</strong> {formatValue(item.settlement_amount, 'currency')}
                                            </div>
                                        )}

                                        {item.net_attorney_fees > 0 && (
                                            <div className='case-card-settlement'>
                                                <strong>Net Attorney Fees:</strong> {formatValue(item.net_attorney_fees, 'currency')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : displayData.length > 0 ? (
                            <div className='no-cases'>
                                <div>No Settlements Match Your Filters</div>
                                <div className='subtext'>
                                    Try adjusting your search criteria.
                                </div>
                            </div>
                        ) : (
                            <div className='no-cases'>
                                <div>No Settlements Found</div>
                                <div className='subtext'>
                                    No settlements found for the selected date range and filters.
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </>
            )}

            {/* New Task Modal */}
            {showNewTaskModal && (
                <Modal
                    title='Create New Task'
                    wide
                    onClose={closeNewTaskModal}
                >
                    <div className="modal-content-wrapper">
                        {/* Case/Lead Selection */}
                        <div className='form-group full'>
                            <label className="subtext" style={{ display: 'block', marginBottom: '8px' }}>
                                Select Case or Lead <span className="subtext" style={{ fontStyle: 'italic' }}>(Optional)</span>
                            </label>
                            <SearchSelect
                                value={selectedCaseOrLead}
                                onChange={(val) => setSelectedCaseOrLead(val)}
                                loadOptions={loadCaseLeadOptions}
                                loadOptionsMinChars={2}
                                placeholder="Search for a case or lead..."
                            />
                        </div>

                        {/* AddActivity Component - locked to tasks mode */}
                        <AddActivity
                            users={users}
                            fetchFeed={refreshAfterTaskCreation}
                            case_id={selectedCaseLeadTarget?.type === 'case' ? selectedCaseLeadTarget.id : null}
                            lead_id={selectedCaseLeadTarget?.type === 'lead' ? selectedCaseLeadTarget.id : null}
                            onClick={() => {}}
                            addActivity={true}
                            setAddActivity={(val) => !val && closeNewTaskModal()}
                            contacts={[]}
                            lockedType={1}
                        />
                    </div>
                </Modal>
            )}

            {/* New Note Modal */}
            {showNewNoteModal && (
                <Modal
                    title='Create New Note'
                    wide
                    onClose={closeNewNoteModal}
                >
                    <div className="modal-content-wrapper">
                        {/* Case/Lead Selection - REQUIRED */}
                        <div className='form-group full'>
                            <label className="subtext" style={{ display: 'block', marginBottom: '8px' }}>
                                Select Case or Lead <span style={{ color: 'red' }}>*</span>
                            </label>
                            <SearchSelect
                                value={selectedCaseOrLead}
                                onChange={(val) => setSelectedCaseOrLead(val)}
                                loadOptions={loadCaseLeadOptions}
                                loadOptionsMinChars={2}
                                placeholder="Search for a case or lead..."
                            />
                            {!selectedCaseOrLead && (
                                <div className="subtext" style={{ color: 'red', marginTop: '4px' }}>
                                    Please select a case or lead to create a note
                                </div>
                            )}
                        </div>

                        {/* AddActivity Component - locked to notes mode */}
                        <AddActivity
                            users={users}
                            fetchFeed={refreshAfterNoteCreation}
                            case_id={selectedCaseLeadTarget?.type === 'case' ? selectedCaseLeadTarget.id : null}
                            lead_id={selectedCaseLeadTarget?.type === 'lead' ? selectedCaseLeadTarget.id : null}
                            onClick={() => {}}
                            addActivity={true}
                            setAddActivity={(val) => !val && closeNewNoteModal()}
                            contacts={[]}
                            lockedType={0}
                            locked={!selectedCaseOrLead}
                        />
                    </div>
                </Modal>
            )}

            {/* Task Detail Modal */}
            {showTaskModal && selectedTask && (() => {
                const currentUserId = parseInt(userId);
                const completionInfo = getTaskCompletionStatus(selectedTask, currentUserId);
                
                return (
                    <Modal
                        title={
                            <>
                                {editingTask ? 'Edit Task' : 'Task Details'}
                                <span
                                    className={`task-status-badge ${
                                        hoveringTaskStatus 
                                            ? (completionInfo.userCompleted ? 'marking-incomplete' : 'marking-complete')
                                            : (completionInfo.status === 'complete' ? 'completed' : 
                                               completionInfo.status === 'partial' ? 'partial' : 
                                               selectedTask.is_overdue ? 'overdue' : 'incomplete')
                                    }`}
                                    onMouseEnter={() => setHoveringTaskStatus(true)}
                                    onMouseLeave={() => setHoveringTaskStatus(false)}
                                    onClick={() => toggleTaskCompletion(selectedTask.id || selectedTask.task_id, completionInfo.userCompleted)}
                                    style={{ cursor: 'pointer' }}
                                    title={completionInfo.status === 'partial' ? 
                                        `${completionInfo.completedCount} of ${completionInfo.totalCount} assignees completed` : ''}
                                >
                                    {hoveringTaskStatus 
                                        ? (completionInfo.userCompleted ? 'Mark Incomplete' : 'Mark Complete')
                                        : (completionInfo.status === 'complete' ? 'Completed' : 
                                           completionInfo.status === 'partial' ? `Partially Complete (${completionInfo.completedCount}/${completionInfo.totalCount})` :
                                           selectedTask.is_overdue ? 'Overdue' : 'Incomplete')
                                    }
                                </span>
                            </>
                        }
                        onClose={closeTaskModal}
                        header={
                        <div className='modal-header-actions'>
                            {user?.user_id === selectedTask.author_id && (
                                editingTask ? (
                                    <>
                                        <button className='action' onClick={saveTaskEdit}>Save</button>
                                        <button className='action destroy' onClick={() => {
                                            setEditingTask(false);
                                            setEditedTaskData({});
                                        }}>Cancel</button>
                                    </>
                                ) : (
                                    <button className='action' onClick={() => {
                                        setEditingTask(true);
                                        const caseLeadKey = selectedTask.case_id ? `case_${selectedTask.case_id}` : 
                                                          selectedTask.lead_id ? `lead_${selectedTask.lead_id}` : '';
                                        setEditedTaskData({
                                            subject: selectedTask.subject,
                                            content: selectedTask.content,
                                            task: selectedTask.assigned_to_id?.[0] || '',
                                            due_date: selectedTask.deadline_due ? selectedTask.deadline_due.split(' ')[0] : '',
                                            post_date: selectedTask.deadline_post ? selectedTask.deadline_post.split(' ')[0] : '',
                                            case_lead_id: caseLeadKey
                                        });
                                    }}>Edit</button>
                                )
                            )}
                            <button className='action alt' onClick={closeTaskModal}>Close</button>
                        </div>
                    }
                    wide
                    overflowy="visible"
                >
                    <div className="modal-content-wrapper" style={{ overflowY: 'visible' }}>
                        {editingTask ? (
                            <>
                                <div className='form-group activity' style={{ width: '100%', maxWidth: '100%' }}>
                                    <label className='activity-type-label subtext'>Subject<span className='required'>*</span></label>
                                    <input
                                        type='text'
                                        placeholder="Task subject"
                                        value={editedTaskData.subject !== undefined ? editedTaskData.subject : selectedTask.subject}
                                        onChange={(e) => setEditedTaskData(prev => ({ ...prev, subject: e.target.value }))}
                                    />
                                </div>
                                <div className='form-group activity' style={{ width: '100%', maxWidth: '100%' }}>
                                    <label className='activity-type-label subtext'>Content<span className='required'>*</span></label>
                                    <textarea
                                        placeholder="Task details"
                                        value={editedTaskData.content !== undefined ? editedTaskData.content : selectedTask.content}
                                        onChange={(e) => setEditedTaskData(prev => ({ ...prev, content: e.target.value }))}
                                        rows={6}
                                    />
                                </div>
                                <div className='form-group activity' style={{ width: '100%', maxWidth: '100%' }}>
                                    <label className='activity-type-label subtext'>Case / Lead</label>
                                    <SearchSelect
                                        value={editedTaskData.case_lead_id ?? ''}
                                        onChange={(val) => setEditedTaskData(prev => ({ ...prev, case_lead_id: val }))}
                                        loadOptions={loadCaseLeadOptions}
                                        loadOptionsMinChars={2}
                                        placeholder="Select a case or lead..."
                                    />
                                </div>
                                <div className='activity-selected-users'>
                                    {selectedTask?.assigned_to_name?.map((name, index) => 
                                        <div key={index} className="assignees-list">
                                            {name} {selectedTask?.assigned_to_user?.[index] && (<span className='subtext'>(@{selectedTask?.assigned_to_user?.[index]})</span>)}
                                            <div className='remove-assignee'>
                                                <X size={12} onClick={() => {
                                                    const assigneeIdToRemove = selectedTask.assigned_to_id?.[index];
                                                    if (assigneeIdToRemove) {
                                                        saveAssigneeChange(null, assigneeIdToRemove);
                                                    }
                                                }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="activity-task-info">
                                    <div className='form-group task-assign activity share'>
                                        <label className='activity-type-label subtext'>Assign to<span className='required'>*</span></label>
                                        <SearchSelect
                                            value={editedTaskData.task ?? ''}
                                            onChange={(val) => setEditedTaskData(prev => ({ ...prev, task: Number(val) }))}
                                            options={(() => {
                                                const userMap = users?.reduce((acc, user) => {
                                                    acc[user.id] = user.name;
                                                    return acc;
                                                }, {});
                                                return userMap || {};
                                            })()}
                                            placeholder="Select a user..."
                                        />
                                    </div>
                                    <div className='form-group task-assign activity share'>
                                        <label className='activity-type-label subtext'>Due date</label>
                                        <DateInput
                                            value={editedTaskData.due_date !== undefined ? editedTaskData.due_date : (selectedTask.deadline_due ? selectedTask.deadline_due.split(' ')[0] : '')}
                                            onChange={(val) => setEditedTaskData(prev => ({ ...prev, due_date: val }))}
                                        />
                                    </div>
                                    <div className='form-group task-assign activity share'>
                                        <label className='activity-type-label subtext'>Start date</label>
                                        <DateInput
                                            value={editedTaskData.post_date !== undefined ? editedTaskData.post_date : (selectedTask.deadline_post ? selectedTask.deadline_post.split(' ')[0] : '')}
                                            onChange={(val) => setEditedTaskData(prev => ({ ...prev, post_date: val }))}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="task-detail-header" style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                                    {selectedTask.subject && (
                                        <h3 style={{ margin: '0', lineHeight: '1.2' }}>{selectedTask.subject}</h3>
                                    )}

                                    <div className="task-detail-meta">
                                        {selectedTask.deadline_due && (
                                            <div className="task-detail-container">
                                                <label className="subtext">Due Date:</label>
                                                <div className="task-detail small-text">
                                                    {new Date(selectedTask.deadline_due).toLocaleDateString()}
                                                </div>
                                            </div>
                                        )}
                                        <div className="task-detail-container">
                                            <label className="subtext">Author:</label>
                                            <div className="task-detail small-text">
                                                {selectedTask.author_name || selectedTask.author_user}<span className='subtext'>{selectedTask.author_user ? ` (@${selectedTask.author_user})` : ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="task-detail-content">
                                    {selectedTask.content && (
                                        <div className="task-content" style={{ marginBottom: '20px' }}>
                                            <div dangerouslySetInnerHTML={{ __html: selectedTask.content }} />
                                        </div>
                                    )}

                                    <div className="task-detail-meta">
                                        <div style={{ display: 'flex', width
                                            : 'fit-content' }}>
                                            <SearchSelect
                                                value={''}
                                                onChange={(val) => {
                                                    const newVal = Number(val);
                                                    saveAssigneeChange(newVal, null);
                                                }}
                                                options={users?.reduce((acc, user) => {
                                                    acc[user.id] = user.name;
                                                    return acc;
                                                }, {}) || {}}
                                                placeholder="Add an assignee..."
                                            />
                                        </div>
                                        <div className="task-assignees">
                                            <label className="subtext">Assigned to:</label>
                                            {selectedTask?.assigned_to_name?.map((name, index) => 
                                                <div key={index} className="assignees-list">
                                                    {name} {selectedTask?.assigned_to_user?.[index] && (<span className='subtext'>(@{selectedTask?.assigned_to_user?.[index]})</span>)}
                                                    <div className='remove-assignee'>
                                                        <X size={12} onClick={() => {
                                                            const assigneeIdToRemove = selectedTask.assigned_to_id?.[index];
                                                            if (assigneeIdToRemove) {
                                                                saveAssigneeChange(null, assigneeIdToRemove);
                                                            }
                                                        }} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {(selectedTask?.case_name || selectedTask?.lead_name) &&
                                        <div className='task-links'>
                                            {selectedTask?.case_name && (
                                                <div className="task-link-info" style={{ width: 'fit-content' }}>
                                                    <a href={`/case/${selectedTask?.case_id}`} target="_blank" rel="noopener noreferrer" className="task-link">
                                                        {selectedTask?.case_name} <span className='subtext'>(case)</span> <ArrowUpRight size={20} className='task-arrow' />
                                                    </a>
                                                </div>
                                            )}

                                            {selectedTask?.lead_name && !selectedTask?.case_name && (
                                                <div className="task-link-info" style={{ width: 'fit-content' }}>
                                                    <a href={`/intake/${selectedTask.lead_id}`} target="_blank" rel="noopener noreferrer" className="task-link">
                                                        <div className='task-info'>
                                                            <span>{selectedTask.lead_name}</span>
                                                            <span style={{ display: 'flex', alignItems: 'center', color: 'var(--subtext-color)', fontSize: '12px', height: '21px' }}>(lead)</span> 
                                                            <ArrowUpRight size={20} className='task-arrow' />
                                                        </div>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    }

                                    {Array.isArray(selectedTask.attachments) && selectedTask.attachments.length > 0 && (
                                        <div className="task-attachments">
                                            <label className="subtext"><strong>Attachments:</strong></label>
                                            <div className="attachments-list">
                                                {selectedTask.attachments.map((file, index) => (
                                                    <div key={index} className="attachment-item">
                                                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                                                            {file.name || `Attachment ${index + 1}`}
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    </Modal>
                );
            })()}
                    </>
                    )}
        </div>
    );
}
